/**
 * Módulo de cena (TfrmSceneModule): o mapa da mesa, aberto a partir de um item
 * "cena" da biblioteca e sincronizado pelo NDB da sala.
 *
 * - Ferramentas: selecionar/mover, mão (arrastar a vista), régua e, para os
 *   mestres, revelar/esconder névoa.
 * - Roda do mouse aproxima no ponto do cursor; botão do meio ou direito arrasta.
 * - Tokens: arrastar move (encaixa na grade; Alt solta livre), duplo clique abre
 *   as propriedades, clique direito abre o menu, Delete apaga, setas empurram.
 * - Jogadores movem só os tokens que o mestre liberou para eles e não veem os
 *   ocultos, a camada do mestre nem o que está sob a névoa.
 * - Ping (como o PingerEngine do original): Alt + clique pinga na hora, em qualquer ferramenta;
 *   segurar o botão parado num ponto (~0,7 s) também. Todos veem uma pedra caindo na água, com
 *   o nome de quem pingou, e uma seta na borda se o ping cai fora da vista; com Shift, o mestre
 *   leva a vista de todos até ali. Espectadores não pingam.
 * - Rabiscos: mestres e jogadores desenham à mão livre, retângulos, elipses e textos;
 *   cada um apaga os próprios, e o mestre limpa os dos jogadores ou todos.
 * - Desfazer/refazer (Ctrl+Z / Ctrl+Y) o que a própria pessoa fez nesta cena;
 *   copiar/colar (Ctrl+C / Ctrl+V) e duplicar (Ctrl+D) tokens, para o mestre.
 * - Imagem do mapa: ao carregar, a grade desenhada nela é detectada e a nossa cai em
 *   cima; sem grade, ela estica para células inteiras. A ferramenta "Alinhar" (mestre)
 *   ajusta à mão, marca células da imagem para sincronizar e redetecta (`mapGrid.ts`).
 */
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent } from 'react';
import { openDoc, releaseDoc } from '../plugins/ndbClient.js';
import { fileUrl, imageSizeAt, uploadRoomFile, useFileUrl } from '../files.js';
import { useT } from '../i18n/index.js';
import { invertOps, type Op, type ReplicaNodeDatabase } from '../ndb.js';
import { COMBAT_TRACKER, TOKEN_CONDITIONS, seesInvisible } from '../shared.js';
import { addActorOps, linkActorOps, readTracker } from '../room/combat/model.js';
import { onSceneDoorLocked, onSceneInvisible, onScenePing, useApp, type ScenePing } from '../store.js';
import { ContextMenu, type MenuAnchor, type MenuEntry } from '../ui/ContextMenu.js';
import { ImagePickerDialog, RPGDESK_FILE, rpgDeskFileId } from '../ui/ImagePicker.js';
import { hasFileDrag, readFileDrag, type DraggedFile } from '../cloud/fileDrag.js';
import { announceUse, nameOf, useSceneBackgrounds } from '../cloud/inRoom.js';
import { AVATAR_DRAG_NAME } from '../social/avatarUrl.js';
import { askConfirm, askText } from '../ui/prompt.js';
import { toast } from '../ui/Toaster.js';
import { isTouch, LONG_PRESS_MS, LONG_PRESS_SLOP } from '../ui/touch.js';
import {
  addDrawingOps,
  addFogOps,
  addTokenOps,
  backgroundOps,
  canEditDrawing,
  placementOps,
  sceneBgRect,
  drawingsContainerOps,
  duplicateTokenOps,
  canMoveToken,
  cellRect,
  clearFogOps,
  gridDistance,
  readPermOps,
  memoryContainerOps,
  memoryOps,
  readScene,
  restackOps,
  setOps,
  snap,
  clampToken,
  conditionOps,
  visibleTokens,
  TOKEN_COLORS,
  TOKEN_LAYERS,
  type DrawingKind,
  type SceneDrawing,
  type SceneModel,
  type SceneToken,
} from './model.js';
import { InvisibleDialog, SceneSettingsDialog, TokenDialog, WallDialog } from './SceneDialogs.js';
import { auraDrawing } from './aura.js';
import { ConditionBadges, ConditionIcon, tokenConditions, useConditionLabel } from './conditions.js';
import { advanceTrack, drawTrack, pathRadius, shortestPath, startTrack, wallBlocker, wallIndex, type Cell, type MoveTrack } from './movePath.js';
import { playDoorSound } from './sounds.js';
import { distanceParts, type Distance } from './units.js';
import { UVTT_FILE, parseUvtt, uvttImage, uvttMaxBytes, uvttOps } from './uvtt.js';
import { formatBytes } from '../bytes.js';
import { measureImageGrid, snapMarksToImage } from './imageGrid.js';
import { boardFor, gridFromMarks, keepNear, outerRect, rectByRounding, rectFromImageGrid, type BgRect, type CellMark } from './mapGrid.js';
import {
  PRESET_ORDER,
  WALL_PRESETS,
  addWallOps,
  doorStateOps,
  cutWallsOps,
  deleteWallsOps,
  ellipsePoints,
  hitVertex,
  hitWall,
  joinWallEndsOps,
  rectPoints,
  setPointsOps,
  simplifyPath,
  snapHalf,
  snapToWallVertex,
  WALL_JOIN,
  wallsContainerOps,
  wallsInRect,
  doorCenter,
  type Wall,
  type WallPreset,
} from './walls.js';
import { DoorsLayer, WALL_COLORS, WallsLayer, type WallDraft } from './WallsLayer.js';
import { SightOutline, VisionFog, useSightState, wallsKey } from './VisionLayer.js';
import {
  ZONE,
  lightTokens,
  litTiles,
  memoryRects,
  memorySize,
  mergeMemory,
  moveRadius,
  moveWithWalls,
  rectsPath,
  seenTiles,
  seesNow,
  sightSegments,
  slideWithWalls,
  toCells,
  tokenCenter,
  tokenSight,
  zoneAt,
  zoneGrid,
  type Viewer,
} from './vision.js';
import { PING_COLOR, PING_RADIUS, PING_SHOW_MS, pingEdge } from './ping.js';
import { pinchView, type PinchStart, type Point } from './pinch.js';
import './scene.css';
import { serverUrl } from '../serverUrl.js';

type Tool = 'select' | 'pan' | 'ruler' | 'reveal' | 'hide' | 'semi' | 'explore' | 'draw' | 'erase' | 'align' | 'walls';
const FOG_TOOLS: readonly Tool[] = ['reveal', 'hide', 'semi', 'explore'];
type WallMode = 'draw' | 'select' | 'cut';
type WallShape = 'line' | 'rect' | 'ellipse' | 'free';
type DrawWidth = 's' | 'm' | 'l';
/** espessura do traço e tamanho do texto, em frações da célula */
const STROKE: Record<DrawWidth, number> = { s: 0.04, m: 0.08, l: 0.16 };
const TEXT_SIZE: Record<DrawWidth, number> = { s: 0.3, m: 0.45, l: 0.7 };
type View = { x: number; y: number; zoom: number };
type Drag =
  | { kind: 'pan'; startX: number; startY: number; view: View }
  /**
   * `x`, `y`: onde o mouse leva o token; `px`, `py`: onde ele está (encaixado e parado nas paredes);
   * `track`: o caminho que ele fez até aqui (`movePath.ts`)
   */
  | { kind: 'token'; id: string; dx: number; dy: number; x: number; y: number; px: number; py: number; moved: boolean; free: boolean; track: MoveTrack | null }
  | { kind: 'ruler' | 'fog'; ax: number; ay: number; bx: number; by: number }
  /** desenho em andamento, em px do mapa */
  | { kind: 'draw'; shape: Exclude<DrawingKind, 'text'>; points: number[]; ax: number; ay: number; bx: number; by: number }
  /** célula da imagem sendo marcada para alinhar, em px do mapa */
  | { kind: 'mark'; ax: number; ay: number; bx: number; by: number }
  /** parede sendo desenhada por forma, em células */
  | { kind: 'wallShape'; shape: Exclude<WallShape, 'line'>; points: number[]; ax: number; ay: number; bx: number; by: number }
  /** caixa de seleção ou de corte das paredes, em células */
  | { kind: 'wallBox'; cut: boolean; add: boolean; ax: number; ay: number; bx: number; by: number }
  /** vértice de parede sendo arrastado, em células */
  | { kind: 'wallVertex'; wallId: string; index: number; x: number; y: number; moved: boolean };

/** Passo do ajuste fino da imagem, em células, e do giro, em graus (Shift: 10×). */
const ALIGN_STEP = 0.01;
const ALIGN_ANGLE_STEP = 0.05;
/** Retângulo (px do mapa) a partir de dois cantos. */
const boxOf = (d: { ax: number; ay: number; bx: number; by: number }) => ({ x: Math.min(d.ax, d.bx), y: Math.min(d.ay, d.by), w: Math.abs(d.bx - d.ax), h: Math.abs(d.by - d.ay) });

/** token copiado (Ctrl+C), entre cenas também; colado em outra mesa, vai sem quem controla nem os liberados do Invisível */
let tokenClipboard: { roomId: string | undefined; token: SceneToken } | null = null;
const UNDO_LIMIT = 50;

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 6;

export function SceneModule({ itemId }: { itemId: string }) {
  const t = useT();
  const [db, setDb] = useState<ReplicaNodeDatabase | null>(null);
  const [version, setVersion] = useState(0);
  const exists = useApp((s) => s.library.some((i) => i.id === itemId));
  // o mestre ocultou a cena aberta (BRAZ-16): um bloco preto até ela voltar
  const hidden = useApp((s) => s.hiddenLibrary.includes(itemId));

  useEffect(() => {
    // só abre o que a pessoa vê; a cena que volta a ser visível abre de novo, com o estado de agora
    if (!exists) return;
    let alive = true;
    let unsubscribe: (() => void) | undefined;
    void openDoc(itemId).then((opened) => {
      if (!alive) return;
      setDb(opened);
      unsubscribe = opened.subscribe(() => setVersion((v) => v + 1));
    });
    return () => {
      alive = false;
      unsubscribe?.();
      releaseDoc(itemId);
      setDb(null);
    };
  }, [itemId, exists]);

  if (hidden) return <div className="rd-module__hidden" role="img" aria-label={t('library.notVisible')} />;
  if (!exists) return <div className="rd-module__placeholder">{t('scene.gone')}</div>;
  if (!db) {
    return (
      <div className="rd-module__placeholder">
        <span className="rd-spinner" /> {t('scene.loading')}
      </div>
    );
  }
  return <SceneView itemId={itemId} db={db} scene={readScene(db.visible())} version={version} />;
}

/** Tempo segurando parado para pingar. */
const PING_HOLD_MS = 700;
/** Quanto tempo o cadeado aparece sobre a porta trancada que alguém tentou abrir. */
const LOCK_FLASH_MS = 1500;

/** `version` muda a cada mudança do documento (a cena é lida de novo a cada render). */
function SceneView({ itemId, db, scene, version }: { itemId: string; db: ReplicaNodeDatabase; scene: SceneModel; version: number }) {
  const t = useT();
  const conditionLabel = useConditionLabel();
  const me = useApp((s) => s.user);
  const room = useApp((s) => s.room);
  const myModes = useApp((s) => (s.user ? s.members[s.user.id]?.modes : undefined));
  const distanceUnit = useApp((s) => s.settings.distanceUnit);
  const isGm = !!me && !!room && (room.ownerId === me.id || !!myModes?.includes('gm'));
  const viewportRef = useRef<HTMLDivElement>(null);
  const [tool, setTool] = useState<Tool>('select');
  const [view, setView] = useState<View>({ x: 0, y: 0, zoom: 1 });
  const [drag, setDrag] = useState<Drag | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ anchor: MenuAnchor; tokenId: string } | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  /** o token com o diálogo do Invisível aberto (BRAZ-24) */
  const [invisibleFor, setInvisibleFor] = useState<string | null>(null);
  const [settings, setSettings] = useState(false);
  const [bgPicker, setBgPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pings, setPings] = useState<(ScenePing & { key: number })[]>([]);
  const [drawKind, setDrawKind] = useState<DrawingKind>('free');
  const [drawColor, setDrawColor] = useState<string>(TOKEN_COLORS[0]!);
  const [drawFill, setDrawFill] = useState(false);
  const [drawWidth, setDrawWidth] = useState<DrawWidth>('m');
  const [clearMenu, setClearMenu] = useState<MenuAnchor | null>(null);
  // as ferramentas de névoa ficam num botão só, com menu (BRAZ-15); o botão mostra a ativa ou a última usada
  const [fogMenu, setFogMenu] = useState<MenuAnchor | null>(null);
  const lastFogTool = useRef<Tool>('reveal');
  if (FOG_TOOLS.includes(tool)) lastFogTool.current = tool;
  // ferramenta "Paredes" (mestre)
  const [wallMode, setWallMode] = useState<WallMode>('draw');
  const [wallShape, setWallShape] = useState<WallShape>('line');
  const [wallPreset, setWallPreset] = useState<WallPreset>('wall');
  const [wallSnap, setWallSnap] = useState(true);
  const [selectedWalls, setSelectedWalls] = useState<ReadonlySet<string>>(new Set());
  /** linha de segmentos sendo desenhada (células) e o ponto sob o mouse */
  const [wallPath, setWallPath] = useState<number[] | null>(null);
  const [wallHover, setWallHover] = useState<{ x: number; y: number } | null>(null);
  /** paredes cujas propriedades estão sendo editadas */
  const [wallDialog, setWallDialog] = useState<string[] | null>(null);
  /** mestre: ver a cena como os jogadores ('all') ou como um jogador (id); null é a vista do mestre */
  const [viewAs, setViewAs] = useState<string | null>(null);
  const members = useApp((s) => s.members);
  const isPlayer = !!myModes?.includes('player');
  // o tracker de combate da sala, aberto para o mestre pôr tokens no combate (BRAZ-24)
  const trackerId = useApp((s) => (isGm ? (s.library.find((i) => i.kind === 'dock' && i.dataType === COMBAT_TRACKER && !i.inactive)?.id ?? null) : null));
  const [trackerDb, setTrackerDb] = useState<ReplicaNodeDatabase | null>(null);
  const [, setTrackerVersion] = useState(0);
  useEffect(() => {
    if (!trackerId) return;
    let alive = true;
    let off: (() => void) | undefined;
    void openDoc(trackerId).then((opened) => {
      if (!alive) return;
      setTrackerDb(opened);
      off = opened.subscribe(() => setTrackerVersion((v) => v + 1));
    });
    return () => {
      alive = false;
      off?.();
      setTrackerDb(null);
      releaseDoc(trackerId);
    };
  }, [trackerId]);
  // jogadores desenham no container da cena; o mestre sempre pode
  const canDraw = isGm || (isPlayer && !!scene.drawingsId);
  const sendRaw = useApp((s) => s.sendRaw);
  const users = useApp((s) => s.users);
  const holdRef = useRef<{ timer: ReturnType<typeof setTimeout>; x: number; y: number } | null>(null);
  /** dedos na área da cena, pela posição na tela; com dois, é a pinça */
  const touchesRef = useRef(new Map<number, Point>());
  /** a pinça em andamento: os dois dedos e a vista no começo (relativos à área da cena) */
  const pinchRef = useRef<(PinchStart & { ids: [number, number] }) | null>(null);
  /** Alt + clique num token: pinga ao soltar, se não arrastou (arrastar com Alt solta o token da grade). */
  const altClickRef = useRef<{ x: number; y: number; at: { x: number; y: number }; shift: boolean } | null>(null);
  /** pingou com Alt: o soltar do Alt é consumido, para não abrir o menu da janela (Electron, Firefox no Windows) */
  const altPinged = useRef(false);
  // como no original, espectadores não pingam
  const canPing = isGm || isPlayer;
  const g = scene.gridSize;
  // o tabuleiro (a grade) é a área de jogo; a imagem pode passar um pouco dele (o pedaço de
  // célula da borda do mapa), e a névoa e o enquadramento cobrem as duas coisas
  const mapW = scene.cols * g;
  const mapH = scene.rows * g;
  const bgRect = sceneBgRect(scene);
  const bgBox = bgRect ? outerRect(bgRect) : null;
  const area = {
    x: Math.min(0, bgBox ? bgBox.x * g : 0),
    y: Math.min(0, bgBox ? bgBox.y * g : 0),
    x2: Math.max(mapW, bgBox ? (bgBox.x + bgBox.w) * g : 0),
    y2: Math.max(mapH, bgBox ? (bgBox.y + bgBox.h) * g : 0),
  };
  /** células da imagem marcadas para alinhar (px do mapa) */
  const [marks, setMarks] = useState<{ x: number; y: number; w: number; h: number }[]>([]);
  const bgFile = useFileUrl(scene.bgFileId);
  // imagem do mapa: arquivo do RPG Desk (link assinado) ou endereço da web
  const bgHref = scene.bgFileId ? (typeof bgFile === 'string' && bgFile !== 'error' ? bgFile : null) : scene.bgUrl ? serverUrl(scene.bgUrl) : scene.bgUrl;
  const tokens = visibleTokens(scene, isGm);
  const selectedToken = tokens.find((tk) => tk.id === selected) ?? null;

  // desfazer/refazer: o inverso de cada escrita minha, calculado antes de aplicar
  const undoStack = useRef<Op[][]>([]);
  const redoStack = useRef<Op[][]>([]);
  const [, setHistoryVersion] = useState(0);
  const write = useCallback(
    (ops: Op[], record = true) => {
      if (!ops.length) return;
      try {
        const inverse = record ? invertOps(db.visible(), ops) : null;
        db.write(ops);
        if (inverse) {
          undoStack.current.push(inverse);
          if (undoStack.current.length > UNDO_LIMIT) undoStack.current.shift();
          redoStack.current = [];
          setHistoryVersion((v) => v + 1);
        }
      } catch (error) {
        toast(t('scene.writeFailed', { message: error instanceof Error ? error.message : String(error) }), 'error');
      }
    },
    [db, t],
  );
  /** Desfaz (ou refaz) a última; se a cena mudou demais (alguém apagou), avisa e descarta. */
  const travel = (from: { current: Op[][] }, to: { current: Op[][] }) => {
    const ops = from.current.pop();
    if (!ops) return;
    try {
      const back = invertOps(db.visible(), ops);
      db.write(ops);
      to.current.push(back);
    } catch {
      toast(t('scene.undo.stale'), 'info');
    }
    setHistoryVersion((v) => v + 1);
  };
  const undo = () => travel(undoStack, redoStack);
  const redo = () => travel(redoStack, undoStack);

  // cena antiga sem o container dos desenhos: o mestre cria (os jogadores não podem)
  const containerAsked = useRef(false);
  useEffect(() => {
    if (!isGm || scene.drawingsId || containerAsked.current) return;
    containerAsked.current = true;
    write(drawingsContainerOps(db.newId(), db.visible().root.id), false);
  }, [isGm, scene.drawingsId, db, write]);

  /* ------------------------------------------------------------------ vista */

  const fit = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    if (!width || !height) return;
    const areaW = area.x2 - area.x;
    const areaH = area.y2 - area.y;
    const zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.min((width - 32) / areaW, (height - 32) / areaH)));
    setView({ zoom, x: (width - areaW * zoom) / 2 - area.x * zoom, y: (height - areaH * zoom) / 2 - area.y * zoom });
  }, [area.x, area.y, area.x2, area.y2]);

  // enquadra ao abrir, quando o tamanho do mapa muda e quando o painel ganha tamanho
  // (a aba pode abrir escondida)
  const fitted = useRef(false);
  useLayoutEffect(() => {
    fitted.current = false;
    fit();
    const el = viewportRef.current;
    if (el && el.getBoundingClientRect().width > 0) fitted.current = true;
  }, [fit]);
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      if (fitted.current || el.getBoundingClientRect().width === 0) return;
      fitted.current = true;
      fit();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [fit]);
  // o tamanho da vista, para a seta do ping que cai fora dela
  const [viewSize, setViewSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      setViewSize((cur) => (cur.w === r.width && cur.h === r.height ? cur : { w: r.width, h: r.height }));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const rotateRef = useRef<((dir: number) => boolean) | null>(null);
  const zoomAt = useCallback((factor: number, cx?: number, cy?: number) => {
    setView((v) => {
      const el = viewportRef.current;
      const r = el?.getBoundingClientRect();
      const px = cx ?? (r ? r.width / 2 : 0);
      const py = cy ?? (r ? r.height / 2 : 0);
      const zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, v.zoom * factor));
      const k = zoom / v.zoom;
      return { zoom, x: px - (px - v.x) * k, y: py - (py - v.y) * k };
    });
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    // passivo: false para poder impedir a rolagem da página
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (e.shiftKey && rotateRef.current?.(Math.sign(e.deltaY || e.deltaX) || 1)) return;
      const r = el.getBoundingClientRect();
      zoomAt(Math.exp(-e.deltaY * 0.0015), e.clientX - r.left, e.clientY - r.top);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  // pings desta cena (de qualquer pessoa, inclusive os meus, que voltam pelo servidor)
  useEffect(
    () =>
      onScenePing((ping) => {
        if (ping.itemId !== itemId) return;
        const key = Date.now() + Math.random();
        setPings((list) => [...list, { ...ping, key }]);
        setTimeout(() => setPings((list) => list.filter((p) => p.key !== key)), PING_SHOW_MS);
        if (ping.focus) {
          const r = viewportRef.current?.getBoundingClientRect();
          if (r) setView((v) => ({ ...v, x: r.width / 2 - ping.x * g * v.zoom, y: r.height / 2 - ping.y * g * v.zoom }));
        }
      }),
    [itemId, g],
  );
  /** Quem pingou: o nick na sala (ou o nome da conta) e a cor da pessoa. */
  const pingOf = (p: ScenePing) => {
    const who = users.find((u) => u.id === p.userId);
    return { name: members[p.userId]?.nick ?? who?.displayName ?? '', color: who?.color ?? PING_COLOR };
  };
  const cancelHold = () => {
    if (holdRef.current) clearTimeout(holdRef.current.timer);
    holdRef.current = null;
  };
  useEffect(() => cancelHold, []);

  /** Ponto da tela → célula do mapa. */
  const toCell = (e: { clientX: number; clientY: number }) => {
    const r = viewportRef.current!.getBoundingClientRect();
    return { x: (e.clientX - r.left - view.x) / view.zoom / g, y: (e.clientY - r.top - view.y) / view.zoom / g };
  };
  /** Ponto da tela → px do mapa. */
  const toPx = (e: { clientX: number; clientY: number }) => {
    const c = toCell(e);
    return { x: c.x * g, y: c.y * g };
  };

  const addText = async (at: { x: number; y: number }) => {
    const text = await askText({ title: t('scene.draw.text'), label: t('scene.draw.textPrompt'), maxLength: 200 });
    if (!text || !scene.drawingsId) return;
    write(
      addDrawingOps(
        scene.drawingsId,
        db.newId(),
        { kind: 'text', points: [], x: at.x, y: at.y, w: 0, h: 0, text, stroke: drawColor, fill: null, width: g * TEXT_SIZE[drawWidth] },
        me?.id ?? null,
      ),
    );
  };

  const commitDrawing = (d: Extract<Drag, { kind: 'draw' }>) => {
    if (!scene.drawingsId) return;
    const stroke = g * STROKE[drawWidth];
    const fill = drawFill ? `${drawColor}55` : null;
    if (d.shape === 'free') {
      if (d.points.length < 4) return;
      write(addDrawingOps(scene.drawingsId, db.newId(), { kind: 'free', points: d.points, x: 0, y: 0, w: 0, h: 0, text: '', stroke: drawColor, fill: null, width: stroke }, me?.id ?? null));
      return;
    }
    const x = Math.min(d.ax, d.bx);
    const y = Math.min(d.ay, d.by);
    const w = Math.abs(d.bx - d.ax);
    const h = Math.abs(d.by - d.ay);
    if (w * view.zoom < 4 && h * view.zoom < 4) return;
    write(addDrawingOps(scene.drawingsId, db.newId(), { kind: d.shape, points: [], x, y, w, h, text: '', stroke: drawColor, fill, width: stroke }, me?.id ?? null));
  };

  const eraseDrawing = (d: SceneDrawing) => {
    if (canEditDrawing(d, me?.id, isGm)) write([{ t: 'del', node: d.id }]);
  };

  const clearDrawings = async (which: 'mine' | 'players' | 'all') => {
    const targets = scene.drawings.filter((d) => (which === 'mine' ? d.owner === me?.id : which === 'players' ? d.owner !== me?.id && isGm : isGm));
    if (!targets.length) return;
    const sure = await askConfirm({ title: t(`scene.draw.clear.${which}`), message: t('scene.draw.clear.confirm', { n: targets.length }), action: t('scene.draw.clear.go'), danger: true });
    if (sure) write(targets.map((d) => ({ t: 'del' as const, node: d.id })));
  };

  /* ------------------------------------------------------------------ paredes */

  /**
   * O ponto desenhado ou arrastado: perto do vértice de outra parede (um quarto de célula, ou alguns
   * pixels quando o mapa está longe), encaixa nele (BRAZ-19); senão, na grade ou no centésimo.
   * `exceptId`: a parede que está sendo mexida.
   */
  const wallPoint = (c: { x: number; y: number }, exceptId?: string) =>
    snapToWallVertex(scene.walls, c.x, c.y, Math.max(WALL_JOIN, 8 / view.zoom / g), exceptId) ??
    (wallSnap ? { x: snapHalf(c.x), y: snapHalf(c.y) } : { x: round2(c.x), y: round2(c.y) });
  /** Porta tem 2 pontos: os tipos de porta desenham só segmentos, um por vez. */
  const doorPreset = wallPreset === 'door' || wallPreset === 'secret';
  const shapeInUse: WallShape = doorPreset ? 'line' : wallShape;

  /** Parede nova (o container nasce junto na primeira parede da cena). */
  const commitWall = (points: number[], closed: boolean) => {
    if (!isGm || points.length < 4) return;
    let containerId = scene.wallsId;
    const ops: Op[] = [];
    if (!containerId) {
      containerId = db.newId();
      ops.push(...wallsContainerOps(containerId, db.visible().root.id));
    }
    ops.push(...addWallOps(containerId, db.newId(), points, closed, WALL_PRESETS[wallPreset], db.newId()));
    write(ops);
  };

  /** Termina a linha de segmentos (aberta, ou fechada se pedido). */
  const finishWallPath = (closed = false) => {
    if (wallPath && wallPath.length >= 4) commitWall(wallPath, closed && wallPath.length >= 6);
    setWallPath(null);
  };

  const deleteSelectedWalls = () => {
    const ids = scene.walls.filter((w) => selectedWalls.has(w.id)).map((w) => w.id);
    if (!ids.length) return;
    write(deleteWallsOps(ids));
    setSelectedWalls(new Set());
  };

  /** "Unir pontas próximas": as selecionadas, ou todas as paredes da cena (BRAZ-19). */
  const joinWallEnds = () => {
    const ids = selectedWalls.size ? selectedWalls : new Set(scene.walls.map((w) => w.id));
    const { ops, joined } = joinWallEndsOps(scene.walls, ids);
    if (!joined) {
      toast(t('scene.walls.join.none'), 'info');
      return;
    }
    write(ops);
    toast(t('scene.walls.join.done', { n: joined }), 'success');
  };

  const clearWalls = async () => {
    if (!scene.walls.length) return;
    const sure = await askConfirm({ title: t('scene.walls.clear'), message: t('scene.walls.clear.confirm', { n: scene.walls.length }), action: t('scene.walls.clear'), danger: true });
    if (sure) {
      write(deleteWallsOps(scene.walls.map((w) => w.id)));
      setSelectedWalls(new Set());
    }
  };

  /** Clique da ferramenta "Paredes" (botão esquerdo, mestre). */
  const onWallsDown = (c: { x: number; y: number }, e: { shiftKey: boolean }) => {
    const tol = 8 / view.zoom / g;
    if (wallMode === 'draw') {
      const p = wallPoint(c);
      if (shapeInUse !== 'line') {
        setDrag({ kind: 'wallShape', shape: shapeInUse, points: [c.x, c.y], ax: p.x, ay: p.y, bx: p.x, by: p.y });
        return;
      }
      const path = wallPath ?? [];
      const n = path.length;
      // clicar no primeiro ponto fecha a linha
      if (n >= 6 && Math.hypot(p.x - path[0]!, p.y - path[1]!) <= tol) {
        finishWallPath(true);
        return;
      }
      // o segundo clique do duplo clique cai no mesmo ponto
      if (n >= 2 && path[n - 2] === p.x && path[n - 1] === p.y) return;
      const next = [...path, p.x, p.y];
      if (doorPreset && next.length === 4) {
        commitWall(next, false);
        setWallPath(null);
        return;
      }
      setWallPath(next);
      return;
    }
    if (wallMode === 'select') {
      const vertex = hitVertex(scene.walls, c.x, c.y, tol);
      if (vertex) {
        setSelectedWalls(new Set([vertex.wall.id]));
        setDrag({ kind: 'wallVertex', wallId: vertex.wall.id, index: vertex.index, x: vertex.wall.points[vertex.index * 2]!, y: vertex.wall.points[vertex.index * 2 + 1]!, moved: false });
        return;
      }
      const wall = hitWall(scene.walls, c.x, c.y, (6 / view.zoom) / g);
      if (wall) {
        setSelectedWalls((cur) => {
          if (!e.shiftKey) return new Set([wall.id]);
          const next = new Set(cur);
          if (next.has(wall.id)) next.delete(wall.id);
          else next.add(wall.id);
          return next;
        });
        return;
      }
    }
    setDrag({ kind: 'wallBox', cut: wallMode === 'cut', add: e.shiftKey, ax: c.x, ay: c.y, bx: c.x, by: c.y });
  };

  /** Fim do arrasto da ferramenta "Paredes". */
  const onWallsUp = (d: Extract<Drag, { kind: 'wallShape' | 'wallBox' | 'wallVertex' }>) => {
    if (d.kind === 'wallShape') {
      const w = Math.abs(d.bx - d.ax);
      const h = Math.abs(d.by - d.ay);
      if (d.shape === 'free') {
        const points = simplifyPath(d.points, 0.08).map(round2);
        const length = points.reduce((sum, _, i) => (i >= 2 && i % 2 === 0 ? sum + Math.hypot(points[i]! - points[i - 2]!, points[i + 1]! - points[i - 1]!) : sum), 0);
        if (points.length >= 4 && length >= 0.25) commitWall(points, false);
      } else if (w >= 0.25 && h >= 0.25) {
        commitWall(d.shape === 'rect' ? rectPoints(d.ax, d.ay, d.bx, d.by) : ellipsePoints(d.ax, d.ay, d.bx, d.by), true);
      }
      return;
    }
    if (d.kind === 'wallVertex') {
      const wall = scene.walls.find((w) => w.id === d.wallId);
      if (wall && d.moved) {
        const points = wall.points.slice();
        points[d.index * 2] = d.x;
        points[d.index * 2 + 1] = d.y;
        write(setPointsOps(wall, points));
      }
      return;
    }
    const box = { x: Math.min(d.ax, d.bx), y: Math.min(d.ay, d.by), w: Math.abs(d.bx - d.ax), h: Math.abs(d.by - d.ay) };
    // clique sem arrastar: só limpa a seleção
    if (box.w * g * view.zoom < 4 && box.h * g * view.zoom < 4) {
      if (!d.cut && !d.add) setSelectedWalls(new Set());
      return;
    }
    if (d.cut) {
      if (scene.wallsId) write(cutWallsOps(scene.walls, box, scene.wallsId, () => db.newId()));
      return;
    }
    const inside = wallsInRect(scene.walls, box).map((w) => w.id);
    setSelectedWalls((cur) => new Set([...(d.add ? cur : []), ...inside]));
  };

  // visão desligada (ou deixou de ser mestre): volta à vista normal
  const visionOn = scene.vision && scene.fogEnabled;
  useEffect(() => {
    if (!visionOn || !isGm) setViewAs(null);
  }, [visionOn, isGm]);

  // com a visão ligada, as paredes param os tokens (o mestre só em "Ver como", como no original)
  const blockMoves = visionOn && (!isGm || viewAs !== null);
  const wallsSig = wallsKey(scene);
  const allSegs = useMemo(() => sightSegments(scene.walls), [wallsSig]); // eslint-disable-line react-hooks/exhaustive-deps
  const moveSegs = useMemo(() => allSegs.filter((s) => s.move), [allSegs]);
  /** Onde o token para indo de onde está até (x, y) (canto superior esquerdo, em células). */
  const stopAtWalls = (token: SceneToken, x: number, y: number): { x: number; y: number } => {
    if (!blockMoves) return { x, y };
    const half = token.size / 2;
    const r = moveWithWalls({ x: token.x + half, y: token.y + half }, { x: x + half, y: y + half }, moveRadius(token.size), moveSegs);
    return r.blocked ? { x: round2(r.x - half), y: round2(r.y - half) } : { x, y };
  };
  /**
   * Um passo do token arrastado, de onde ele está até (x, y) (canto superior esquerdo, em células).
   * Como no original, as paredes são testadas a cada passo, a partir de onde o token já está: assim
   * ele dá a volta nelas acompanhando o mouse. **(RPG Desk)** Ao encostar, desliza ao longo da parede.
   */
  const dragStep = (token: SceneToken, from: { x: number; y: number }, x: number, y: number): { x: number; y: number } => {
    if (!blockMoves) return { x, y };
    const half = token.size / 2;
    const r = slideWithWalls({ x: from.x + half, y: from.y + half }, { x: x + half, y: y + half }, moveRadius(token.size), moveSegs);
    return r.blocked ? { x: round2(r.x - half), y: round2(r.y - half) } : { x, y };
  };
  const moveIndex = useMemo(() => wallIndex(moveSegs), [moveSegs]);
  /**
   * O caminho do token arrastado, que está em `at` com o mouse querendo levá-lo a `aim`, como no
   * original: só os da camada dos tokens; com as paredes valendo, ele as contorna (e vai por cima
   * delas se não houver como).
   */
  const trackMove = (token: SceneToken, track: MoveTrack | null, at: { x: number; y: number }, aim: { x: number; y: number }, free: boolean): MoveTrack | null => {
    if (token.layer !== 'tokens') return null;
    const now = performance.now();
    // encaixado, o caminho passa pelos centros das células, mesmo saindo de um token solto
    const base = track ?? startTrack(free ? token : { x: snap(token.x, token.size), y: snap(token.y, token.size), size: token.size }, now);
    const blocked = blockMoves ? wallBlocker(moveIndex, pathRadius(token.size), base.ox, base.oy) : undefined;
    const find = (from: Cell, to: Cell, diagonalsSoFar: number) => {
      const options = { cols: scene.cols, rows: scene.rows, diagonalsSoFar };
      return (blocked && shortestPath(from, to, { ...options, blocked })) || shortestPath(from, to, options);
    };
    const pointer = { i: Math.floor(aim.x + token.size / 2), j: Math.floor(aim.y + token.size / 2) };
    return advanceTrack(base, { ...at, size: token.size }, pointer, now, free, find);
  };

  // fora da ferramenta (ou sem ser mestre), nada fica no meio do caminho
  useEffect(() => {
    if (tool !== 'walls' || !isGm) {
      setWallPath(null);
      setWallHover(null);
      setSelectedWalls(new Set());
    }
  }, [tool, isGm]);
  useEffect(() => {
    setWallPath(null);
    if (wallMode !== 'select') setSelectedWalls(new Set());
  }, [wallMode, wallShape, wallPreset]);
  // paredes apagadas (por mim ou pelo desfazer) saem da seleção
  useEffect(() => {
    setSelectedWalls((cur) => (cur.size && [...cur].some((id) => !scene.walls.some((w) => w.id === id)) ? new Set([...cur].filter((id) => scene.walls.some((w) => w.id === id))) : cur));
  }, [scene.walls]);

  /* ------------------------------------------------------------------ ponteiro */

  /** Ping no ponto (células); o servidor devolve a todos, inclusive a mim. Shift: o mestre traz a vista de todos. */
  const sendPing = (at: { x: number; y: number }, shift: boolean) => {
    if (!canPing) {
      toast(t('scene.ping.spectator'), 'info');
      return;
    }
    sendRaw({ type: 'scene.ping', itemId, x: round2(at.x), y: round2(at.y), focus: shift && isGm });
  };

  /**
   * Segurar parado = ping, no mapa ou sobre um token. Com `token` (o dedo no token que a pessoa
   * controla), segurar abre o menu dele, que no mouse é o botão direito.
   */
  const startHold = (e: { clientX: number; clientY: number; shiftKey: boolean }, token?: SceneToken) => {
    cancelHold();
    if (!token && !canPing) return;
    const at = toCell(e);
    holdRef.current = {
      x: e.clientX,
      y: e.clientY,
      timer: setTimeout(
        () => {
          holdRef.current = null;
          setDrag(null);
          if (token) {
            setSelected(token.id);
            setMenu({ anchor: { x: e.clientX, y: e.clientY }, tokenId: token.id });
          } else sendPing(at, e.shiftKey);
        },
        token ? LONG_PRESS_MS : PING_HOLD_MS,
      ),
    };
  };

  /** Ponto da tela → relativo à área da cena (onde a vista mede `x`/`y`). */
  const inViewport = (p: Point): Point => {
    const r = viewportRef.current!.getBoundingClientRect();
    return { x: p.x - r.left, y: p.y - r.top };
  };
  /** A vista que a pinça dá com os dedos onde estão agora. */
  const pinchNow = (pinch: PinchStart & { ids: [number, number] }): View => {
    const a = touchesRef.current.get(pinch.ids[0]);
    const b = touchesRef.current.get(pinch.ids[1]);
    return a && b ? pinchView(pinch, inViewport(a), inViewport(b), { min: MIN_ZOOM, max: MAX_ZOOM }) : pinch.view;
  };
  /** Os dedos mudaram: com dois ou mais, a pinça recomeça de `from`; com um só, ele passa a mover a vista. */
  const regrip = (from: View) => {
    const fingers = [...touchesRef.current];
    pinchRef.current = null;
    if (fingers.length >= 2) {
      const [[ia, a], [ib, b]] = fingers as [[number, Point], [number, Point]];
      pinchRef.current = { view: from, a: inViewport(a), b: inViewport(b), ids: [ia, ib] };
    } else if (fingers.length === 1) {
      const [, p] = fingers[0]!;
      setDrag({ kind: 'pan', startX: p.x, startY: p.y, view: from });
    }
  };
  /**
   * O dedo que desce (antes do token e do mapa): o segundo começa a pinça, e o que o primeiro
   * começou (segurar, arrastar um token, um traço) fica de lado.
   */
  const onTouchDown = (e: RPointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'touch') return;
    // o primeiro dedo de um toque novo: algum que saiu sem avisar não conta mais
    if (e.isPrimary) touchesRef.current.clear();
    touchesRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (touchesRef.current.size < 2) return;
    e.stopPropagation();
    if (pinchRef.current) return;
    cancelHold();
    altClickRef.current = null;
    setDrag(null);
    regrip(view);
  };
  /** Um dedo saiu. Era da pinça: os que ficam seguem (`true`, o soltar não faz mais nada). */
  const onTouchEnd = (e: RPointerEvent<HTMLDivElement>): boolean => {
    if (!touchesRef.current.has(e.pointerId)) return false;
    const pinch = pinchRef.current;
    const now = pinch && pinchNow(pinch);
    touchesRef.current.delete(e.pointerId);
    if (!now) return false;
    regrip(now);
    return true;
  };

  const onViewportDown = (e: RPointerEvent<HTMLDivElement>) => {
    viewportRef.current?.focus({ preventScroll: true });
    // Alt + clique: ping na hora, em qualquer ferramenta (como o original); o clique não faz mais nada
    if (e.button === 0 && e.altKey) {
      e.preventDefault();
      altPinged.current = true;
      sendPing(toCell(e), e.shiftKey);
      return;
    }
    if (e.button === 0 && (tool === 'select' || tool === 'pan')) startHold(e);
    if (e.button === 1 || e.button === 2 || tool === 'pan' || (tool === 'select' && e.button === 0)) {
      if (e.button === 0 && tool === 'select') setSelected(null);
      e.currentTarget.setPointerCapture(e.pointerId);
      setDrag({ kind: 'pan', startX: e.clientX, startY: e.clientY, view });
      return;
    }
    if (e.button !== 0) return;
    const c = toCell(e);
    if (tool === 'draw' && canDraw) {
      const p = toPx(e);
      if (drawKind === 'text') {
        void addText(p);
        return;
      }
      setDrag({ kind: 'draw', shape: drawKind, points: [p.x, p.y], ax: p.x, ay: p.y, bx: p.x, by: p.y });
    }
    if (tool === 'ruler') setDrag({ kind: 'ruler', ax: c.x, ay: c.y, bx: c.x, by: c.y });
    if (tool === 'align' && isGm) {
      const p = toPx(e);
      setDrag({ kind: 'mark', ax: p.x, ay: p.y, bx: p.x, by: p.y });
    }
    if (FOG_TOOLS.includes(tool) && isGm) setDrag({ kind: 'fog', ax: c.x, ay: c.y, bx: c.x, by: c.y });
    if (tool === 'walls' && isGm) onWallsDown(c, e);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onTokenDown = (token: SceneToken) => (e: RPointerEvent<SVGGElement>) => {
    // botão direito no token é o menu dele, não o arrasto da vista
    if (e.button === 2) {
      e.stopPropagation();
      return;
    }
    if (e.button !== 0 || tool !== 'select') return;
    e.stopPropagation();
    viewportRef.current?.focus({ preventScroll: true });
    const mine = canMoveToken(token, me?.id, isGm);
    if (e.altKey) {
      altPinged.current = true;
      altClickRef.current = { x: e.clientX, y: e.clientY, at: toCell(e), shift: e.shiftKey };
    } else startHold(e, mine && isTouch(e) ? token : undefined);
    setSelected(token.id);
    if (!mine) return;
    const c = toCell(e);
    // a captura só começa quando o arrasto começa (onMove): capturar já aqui mandaria
    // o clique e o duplo clique para o painel em vez do token
    setDrag({ kind: 'token', id: token.id, dx: c.x - token.x, dy: c.y - token.y, x: token.x, y: token.y, px: token.x, py: token.y, moved: false, free: e.altKey, track: null });
  };

  const onMove = (e: RPointerEvent<HTMLDivElement>) => {
    lastPointer.current = toCell(e);
    if (touchesRef.current.has(e.pointerId)) touchesRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinchRef.current) {
      setView(pinchNow(pinchRef.current));
      return;
    }
    // mexeu: não é ping (o dedo treme mais que o mouse)
    if (holdRef.current && Math.hypot(e.clientX - holdRef.current.x, e.clientY - holdRef.current.y) > (e.pointerType === 'touch' ? LONG_PRESS_SLOP : 5)) cancelHold();
    if (altClickRef.current && Math.hypot(e.clientX - altClickRef.current.x, e.clientY - altClickRef.current.y) > 5) altClickRef.current = null;
    if (tool === 'walls' && wallMode === 'draw' && shapeInUse === 'line') {
      const p = wallPoint(toCell(e));
      if (p.x !== wallHover?.x || p.y !== wallHover?.y) setWallHover(p);
    }
    if (!drag) return;
    if (drag.kind === 'pan') {
      setView({ ...drag.view, x: drag.view.x + e.clientX - drag.startX, y: drag.view.y + e.clientY - drag.startY });
      return;
    }
    if (drag.kind === 'mark') {
      const p = toPx(e);
      setDrag({ ...drag, bx: p.x, by: p.y });
      return;
    }
    if (drag.kind === 'wallShape') {
      const c = toCell(e);
      const p = wallPoint(c);
      const n = drag.points.length;
      const points = drag.shape === 'free' && Math.hypot(c.x - drag.points[n - 2]!, c.y - drag.points[n - 1]!) * g * view.zoom >= 3 ? [...drag.points, c.x, c.y] : drag.points;
      setDrag({ ...drag, points, bx: p.x, by: p.y });
      return;
    }
    if (drag.kind === 'wallBox') {
      const c = toCell(e);
      setDrag({ ...drag, bx: c.x, by: c.y });
      return;
    }
    if (drag.kind === 'wallVertex') {
      const p = wallPoint(toCell(e), drag.wallId);
      if (p.x !== drag.x || p.y !== drag.y) setDrag({ ...drag, x: p.x, y: p.y, moved: true });
      return;
    }
    if (drag.kind === 'draw') {
      const p = toPx(e);
      const n = drag.points.length;
      // traço livre: só guarda ponto que andou alguns px na tela
      const points = drag.shape === 'free' && Math.hypot(p.x - drag.points[n - 2]!, p.y - drag.points[n - 1]!) * view.zoom >= 3 ? [...drag.points, p.x, p.y] : drag.points;
      setDrag({ ...drag, points, bx: p.x, by: p.y });
      return;
    }
    const c = toCell(e);
    if (drag.kind === 'token') {
      const x = c.x - drag.dx;
      const y = c.y - drag.dy;
      // tremida do clique não conta como arrasto
      if (!drag.moved && Math.hypot(x - drag.x, y - drag.y) * g * view.zoom < 3) return;
      const token = scene.tokens.find((tk) => tk.id === drag.id);
      if (!token) {
        setDrag(null);
        return;
      }
      if (!drag.moved) e.currentTarget.setPointerCapture(e.pointerId);
      // encaixado na grade (solto com Alt), e a parede testada a partir de onde o token está
      const free = e.altKey;
      const aim = free ? { x, y } : { x: snap(x, token.size), y: snap(y, token.size) };
      const at = dragStep(token, { x: drag.px, y: drag.py }, aim.x, aim.y);
      setDrag({ ...drag, x, y, px: at.x, py: at.y, moved: true, free, track: trackMove(token, drag.track, at, aim, free) });
    } else setDrag({ ...drag, bx: c.x, by: c.y });
  };

  const onUp = () => {
    cancelHold();
    const altClick = altClickRef.current;
    altClickRef.current = null;
    if (altClick) sendPing(altClick.at, altClick.shift);
    if (!drag) return;
    if (drag.kind === 'token' && drag.moved) {
      const token = scene.tokens.find((tk) => tk.id === drag.id);
      if (token) {
        const x = round2(drag.px);
        const y = round2(drag.py);
        if (x !== token.x || y !== token.y) write(setOps(token.id, clampToken(x, y, token.size, scene.cols, scene.rows)));
      }
    }
    if (drag.kind === 'draw') commitDrawing(drag);
    if (drag.kind === 'wallShape' || drag.kind === 'wallBox' || drag.kind === 'wallVertex') onWallsUp(drag);
    if (drag.kind === 'mark') {
      const box = boxOf(drag);
      // clique sem arrastar não marca
      if (box.w * view.zoom >= 6 && box.h * view.zoom >= 6) setMarks((list) => [...list, box]);
    }
    if (drag.kind === 'fog') {
      const rect = cellRect(drag.ax, drag.ay, drag.bx, drag.by);
      const mode = tool === 'hide' || tool === 'semi' || tool === 'explore' ? tool : 'reveal';
      write([...(scene.fogEnabled ? [] : setOps('root', { fogEnabled: true })), ...addFogOps(db.newId(), { ...rect, mode })]);
    }
    setDrag(null);
  };

  /* ------------------------------------------------------------------ ações */

  const addToken = (extra: { fileId?: string; name?: string; x?: number; y?: number } = {}) => {
    const r = viewportRef.current?.getBoundingClientRect();
    const center = r ? toCell({ clientX: r.left + r.width / 2, clientY: r.top + r.height / 2 }) : { x: 0, y: 0 };
    const id = db.newId();
    const n = scene.tokens.length + 1;
    write(
      addTokenOps(
        id,
        {
          name: extra.name ?? t('scene.token.defaultName', { n }),
          x: clampTo(Math.floor(extra.x ?? center.x), 0, scene.cols - 1),
          y: clampTo(Math.floor(extra.y ?? center.y), 0, scene.rows - 1),
          ...(extra.fileId ? { fileId: extra.fileId } : {}),
        },
        scene,
      ),
    );
    setSelected(id);
    setTool('select');
    return id;
  };

  const deleteToken = async (token: SceneToken) => {
    if (!isGm) return;
    const sure = await askConfirm({
      title: t('scene.token.delete'),
      message: t('scene.token.deleteConfirm', { name: token.name || '—' }),
      action: t('scene.token.delete'),
      danger: true,
    });
    if (sure) {
      write([{ t: 'del', node: token.id }]);
      setSelected(null);
    }
  };

  /** Mapa UVTT (Dungeondraft e outros): imagem na grade, paredes, portas e luzes, num lote só. */
  const importUvtt = async (file: File) => {
    if (!isGm) return;
    // o maior arquivo do plano (docs/spec/planos.md); sem ele ainda, quem confere é o servidor
    const maxFile = useApp.getState().plan?.entitlements.maxFileBytes ?? Number.POSITIVE_INFINITY;
    if (file.size > uvttMaxBytes(maxFile)) {
      toast(t('scene.uvtt.tooBig', { size: formatBytes(uvttMaxBytes(maxFile)) }), 'error');
      return;
    }
    setBusy(true);
    try {
      const map = parseUvtt(await file.text());
      const img = uvttImage(map, maxFile);
      const stored = await uploadRoomFile(new File([img.bytes as BlobPart], `${file.name.replace(UVTT_FILE, '') || 'mapa'}.${img.ext}`, { type: img.mime }), 'map');
      const size = await imageSizeAt(await fileUrl(stored.id));
      write(uvttOps(map, scene, { rootId: db.visible().root.id, fileId: stored.id, size, newId: () => db.newId(), lightName: t('scene.uvtt.light') }));
      setMarks([]);
      toast(t('scene.uvtt.done', { walls: map.lines.length, doors: map.portals.length, lights: map.lights.length }), 'info');
    } catch (error) {
      toast(t('scene.uvtt.failed', { message: error instanceof Error ? error.message : String(error) }), 'error');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Um arquivo do Desk Cloud arrastado do painel Arquivos (docs/spec/desk-cloud.md): o mapa vira o
   * fundo (confirmando, se já há um), a outra imagem vira token no ponto.
   */
  const onDropCloudFile = async (file: DraggedFile, at: { clientX: number; clientY: number }) => {
    if (!isGm) return;
    if (!file.mime.startsWith('image/')) {
      toast(t('scene.drop.notImage'), 'error');
      return;
    }
    if (file.kind === 'map') {
      const replace = !scene.bgFileId && !scene.bgUrl ? true : await askConfirm({ title: t('scene.drop.bgTitle'), message: t('scene.drop.bgConfirm', { name: nameOf(file.name) }), action: t('scene.drop.bgReplace') });
      if (replace) await setBackground(`${RPGDESK_FILE}${file.id}`);
      return;
    }
    const c = toCell(at);
    addToken({ fileId: file.id, name: nameOf(file.name).slice(0, 40), x: c.x, y: c.y });
  };

  // o fundo desta cena, para o selo "Na cena" do painel Arquivos (docs/spec/desk-cloud.md)
  useEffect(() => useSceneBackgrounds.getState().report(itemId, scene.bgFileId ?? null), [itemId, scene.bgFileId]);
  useEffect(() => () => useSceneBackgrounds.getState().drop(itemId), [itemId]);

  // o "Usar na mesa" do Desk Cloud (docs/spec/desk-cloud.md): o fundo e o token que chegaram de lá. A cena
  // nova pega só a dela; sem cena marcada, a primeira cena aberta que pegar faz
  const pendingUse = useApp((s) => s.pendingFileUse);
  useEffect(() => {
    if (!isGm || !pendingUse || pendingUse.roomId !== room?.id) return;
    if (pendingUse.action !== 'background' && pendingUse.action !== 'token') return;
    if (pendingUse.itemId && pendingUse.itemId !== itemId) return;
    if (!useApp.getState().takePendingFileUse(pendingUse.nonce)) return;
    if (pendingUse.action === 'background') void setBackground(`${RPGDESK_FILE}${pendingUse.fileId}`);
    else addToken({ fileId: pendingUse.fileId, name: nameOf(pendingUse.name).slice(0, 40) });
    announceUse(pendingUse.itemId ? 'scene' : pendingUse.action, pendingUse.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingUse, isGm, room?.id, itemId]);

  /** `personName`: a foto de alguém arrastada da lista de participantes (o token leva o nome dele). */
  const onDropFiles = async (files: FileList, at: { clientX: number; clientY: number }, personName = '') => {
    if (!isGm) return;
    const uvtt = [...files].find((f) => UVTT_FILE.test(f.name));
    if (uvtt) {
      await importUvtt(uvtt);
      return;
    }
    const images = [...files].filter((f) => f.type.startsWith('image/'));
    if (!images.length) return;
    setBusy(true);
    try {
      let c = toCell(at);
      for (const file of images) {
        const stored = await uploadRoomFile(file, 'token');
        const name = (images.length === 1 && personName.trim()) || file.name.replace(/\.[^.]+$/, '');
        addToken({ fileId: stored.id, name: name.slice(0, 40), x: c.x, y: c.y });
        c = { x: c.x + 1, y: c.y };
      }
    } catch (error) {
      toast(t('scene.uploadFailed', { message: error instanceof Error ? error.message : String(error) }), 'error');
    } finally {
      setBusy(false);
    }
  };

  /** Imagem do mapa escolhida no seletor: `rpgdesk-file:<id>` (enviada ou da mesa) ou um endereço http(s). */
  const setBackground = async (value: string) => {
    setBgPicker(false);
    setBusy(true);
    try {
      const fileId = rpgDeskFileId(value);
      const href = fileId ? await fileUrl(fileId) : value;
      const size = await imageSizeAt(href);
      // grade desenhada na imagem: a nossa cai em cima dela (e a imagem gira, se a grade
      // estiver torta); sem grade, células inteiras
      const found = await measureImageGrid(href);
      const rect = found ? rectFromImageGrid(size, found.grid, found.angle) : rectByRounding(size, g);
      write(backgroundOps(fileId ? { fileId } : { url: value }, size, rect));
      setMarks([]);
      const board = boardFor(rect);
      toast(t(found ? 'scene.align.detected' : 'scene.align.rounded', { cols: board.cols, rows: board.rows }), 'info');
    } catch (error) {
      toast(t('scene.bg.failed', { message: error instanceof Error ? error.message : String(error) }), 'error');
    } finally {
      setBusy(false);
    }
  };

  /* ------------------------------------------------------------------ alinhar */

  /** Passo dos botões de ajuste (Shift: 10×). */
  const alignStep = (e: { shiftKey: boolean }) => ALIGN_STEP * (e.shiftKey ? 10 : 1);
  const cells = (v: number) => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  /** Giro dos botões, em graus (Shift: 10×). */
  const angleStep = (e: { shiftKey: boolean }) => ALIGN_ANGLE_STEP * (e.shiftKey ? 10 : 1);
  const degrees = (v: number) => `${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}°`;
  // sem imagem (ou deixou de ser mestre): sai do alinhar; as marcas valem só enquanto alinha
  useEffect(() => {
    if (tool === 'align' && (!bgRect || !isGm)) setTool('select');
  }, [tool, bgRect, isGm]);
  useEffect(() => {
    if (tool !== 'align') setMarks([]);
  }, [tool]);

  /** Move ou estica a imagem (px do mapa não mudam: tokens e desenhos ficam onde estão). */
  const nudge = (change: Partial<BgRect>) => {
    if (!bgRect || !isGm) return;
    write(placementOps({ ...bgRect, ...change }));
  };
  /** Imagem nova no lugar: a grade que a imagem tem passa a valer, e o tabuleiro a cobre. */
  const realign = (rect: BgRect) => {
    if (!bgRect) return;
    const next = keepNear(rect, bgRect);
    write([...placementOps(next), ...setOps('root', boardFor(next))]);
  };
  /** Marca (px do mapa) → px da imagem, pelo lugar atual da imagem. */
  const markToImage = (m: { x: number; y: number; w: number; h: number }, rect: BgRect): CellMark => {
    const ix = (px: number) => ((px / g - rect.x) / rect.w) * scene.bgW;
    const iy = (px: number) => ((px / g - rect.y) / rect.h) * scene.bgH;
    return { left: ix(m.x), top: iy(m.y), right: ix(m.x + m.w), bottom: iy(m.y + m.h) };
  };
  const syncMarks = async () => {
    if (!bgRect || !bgHref || !marks.length) return;
    const approx = gridFromMarks(marks.map((m) => markToImage(m, bgRect)));
    if (!approx) return;
    setBusy(true);
    try {
      // as linhas da imagem, se houver, acertam o que as marcas aproximaram (na imagem
      // como ela está na cena, com o giro que tiver)
      const angle = bgRect.angle ?? 0;
      const grid = await snapMarksToImage(bgHref, approx, angle);
      realign(rectFromImageGrid({ width: scene.bgW, height: scene.bgH }, grid, angle));
      setMarks([]);
      toast(t('scene.align.synced'), 'info');
    } finally {
      setBusy(false);
    }
  };
  const detectAgain = async () => {
    if (!bgRect || !bgHref) return;
    setBusy(true);
    try {
      const size = { width: scene.bgW, height: scene.bgH };
      const found = await measureImageGrid(bgHref);
      // sem grade desenhada: células inteiras, como ao carregar (conserta cena antiga, no tamanho natural)
      const rect = found ? rectFromImageGrid(size, found.grid, found.angle) : rectByRounding(size, g);
      realign(rect);
      toast(t(found ? 'scene.align.redetected' : 'scene.align.none', { cols: Math.round(rect.w), rows: Math.round(rect.h) }), 'info');
    } finally {
      setBusy(false);
    }
  };

  const duplicate = (token: SceneToken, at?: { x: number; y: number }) => {
    if (!isGm) return;
    const id = db.newId();
    const dx = at ? Math.round(at.x) - token.x : 1;
    const dy = at ? Math.round(at.y) - token.y : 0;
    write(duplicateTokenOps(id, token, scene, dx, dy, () => db.newId()));
    setSelected(id);
  };
  const lastPointer = useRef<{ x: number; y: number } | null>(null);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.target !== e.currentTarget) return;
    const mod = e.ctrlKey || e.metaKey;
    if (tool === 'walls' && isGm && !mod) {
      if (e.key === 'Enter' && wallPath) {
        e.preventDefault();
        finishWallPath();
        return;
      }
      if (e.key === 'Escape' && (wallPath || selectedWalls.size)) {
        e.preventDefault();
        setWallPath(null);
        setSelectedWalls(new Set());
        return;
      }
      if (e.key === 'Backspace' && wallPath) {
        e.preventDefault();
        setWallPath(wallPath.length > 2 ? wallPath.slice(0, -2) : null);
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedWalls.size) {
        e.preventDefault();
        deleteSelectedWalls();
        return;
      }
    }
    if (mod && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      if (e.shiftKey) redo();
      else undo();
      return;
    }
    if (mod && e.key.toLowerCase() === 'y') {
      e.preventDefault();
      redo();
      return;
    }
    if (mod && e.key.toLowerCase() === 'c' && selectedToken && isGm) {
      e.preventDefault();
      tokenClipboard = { roomId: room?.id, token: selectedToken };
      toast(t('scene.token.copied', { name: selectedToken.name || t('scene.token') }), 'info');
      return;
    }
    if (mod && e.key.toLowerCase() === 'v' && tokenClipboard && isGm) {
      e.preventDefault();
      const { roomId, token } = tokenClipboard;
      duplicate(roomId === room?.id ? token : { ...token, controllers: [], invisible: token.invisible && [] }, lastPointer.current ?? undefined);
      return;
    }
    if (mod && e.key.toLowerCase() === 'd' && selectedToken && isGm) {
      e.preventDefault();
      duplicate(selectedToken);
      return;
    }
    const step = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key];
    if (step && tool === 'align' && bgRect && isGm) {
      // alinhando: as setas movem a imagem (Shift: passo maior)
      e.preventDefault();
      const k = ALIGN_STEP * (e.shiftKey ? 10 : 1);
      nudge({ x: bgRect.x + step[0]! * k, y: bgRect.y + step[1]! * k });
      return;
    }
    if (step && selectedToken && canMoveToken(selectedToken, me?.id, isGm)) {
      e.preventDefault();
      const to = stopAtWalls(selectedToken, selectedToken.x + step[0]!, selectedToken.y + step[1]!);
      if (to.x !== selectedToken.x || to.y !== selectedToken.y) write(setOps(selectedToken.id, to));
      return;
    }
    if ((e.key === 'Delete' || e.key === 'Backspace') && selectedToken && isGm) {
      e.preventDefault();
      void deleteToken(selectedToken);
      return;
    }
    if (e.key === 'Escape') setSelected(null);
    if (e.key === '+' || e.key === '=') zoomAt(1.2);
    if (e.key === '-') zoomAt(1 / 1.2);
    if (e.key === '0') fit();
    const shortcut: Record<string, Tool> = { v: 'select', h: 'pan', r: 'ruler', ...(isGm ? { f: 'reveal' as Tool, w: 'walls' as Tool } : {}), ...(canDraw ? { d: 'draw' as Tool, e: 'erase' as Tool } : {}) };
    const next = shortcut[e.key.toLowerCase()];
    if (next && !e.ctrlKey && !e.metaKey) setTool(next);
  };

  /** Gira o token (graus no sentido do relógio); quem o controla pode. */
  const turnToken = (token: SceneToken, by: number) => {
    if (!canMoveToken(token, me?.id, isGm)) return;
    const facing = (((token.facing + by) % 360) + 360) % 360;
    write(setOps(token.id, { facing: facing || null }));
  };
  // Shift + roda gira o token selecionado (15° por passo), em vez de aproximar
  rotateRef.current = (dir: number) => {
    if (!selectedToken || !canMoveToken(selectedToken, me?.id, isGm)) return false;
    turnToken(selectedToken, dir * 15);
    return true;
  };

  /**
   * Põe o token no tracker de combate, ligado a ele (BRAZ-24): invisível, ele some do combate para
   * quem não o vê. Um ator sem ligação com o mesmo nome (os jogadores que entraram pelo tracker) é
   * ligado; senão, entra um novo, do lado de quem controla o token (aliado) ou hostil.
   */
  const addToCombat = (token: SceneToken) => {
    if (!trackerDb) return;
    const doc = trackerDb.visible();
    const tracker = readTracker(doc);
    const name = token.name || t('scene.token');
    const link = { sceneId: itemId, tokenId: token.id };
    const same = tracker.actors.find((a) => !a.sceneId && a.name.trim().toLowerCase() === name.trim().toLowerCase());
    const state = { hidden: token.hidden, layer: token.layer, invisible: token.invisible, controllers: token.controllers };
    const order = Math.max(0, ...tracker.actors.map((a) => a.order + 1));
    try {
      trackerDb.write(same ? linkActorOps(same.id, link) : addActorOps(doc.root.id, trackerDb.newId(), trackerDb.newId(), { name, side: token.controllers.length ? 'friend' : 'foe', link: { ...link, token: state } }, order));
      toast(t('scene.token.addedToCombat', { name }), 'info');
    } catch (error) {
      toast(t('scene.writeFailed', { message: error instanceof Error ? error.message : String(error) }), 'error');
    }
  };

  const tokenMenu = (token: SceneToken): MenuEntry[] => {
    const mine = canMoveToken(token, me?.id, isGm);
    const entries: MenuEntry[] = [{ kind: 'title', label: token.name || t('scene.token') }];
    if (mine) entries.push({ label: t('scene.token.properties'), onSelect: () => setEditing(token.id) });
    if (mine) {
      entries.push({ label: t('scene.token.turnLeft'), hint: t('scene.token.turnHint'), onSelect: () => turnToken(token, -45) });
      entries.push({ label: t('scene.token.turnRight'), hint: t('scene.token.turnHint'), onSelect: () => turnToken(token, 45) });
      // condições (BRAZ-25) e o Invisível (BRAZ-24), que abre o diálogo de quem continua vendo: o
      // mestre em qualquer token, quem controla no dele
      entries.push({
        kind: 'submenu',
        label: t('scene.token.conditions'),
        entries: [
          ...TOKEN_CONDITIONS.map((id): MenuEntry => {
            const on = token.conditions.includes(id);
            return { label: conditionLabel(id), icon: <ConditionIcon id={id} />, toggle: true, checked: on, onSelect: () => write(conditionOps(token.id, id, !on)) };
          }),
          { kind: 'separator' },
          { label: t(token.invisible ? 'scene.token.invisible.on' : 'scene.token.invisible'), icon: <ConditionIcon id="invisible" />, hint: t('scene.token.invisible.hint'), onSelect: () => setInvisibleFor(token.id) },
        ],
      });
    }
    if (isGm) {
      entries.push({
        label: token.hidden ? t('scene.token.show') : t('scene.token.hide'),
        onSelect: () => {
          write([...setOps(token.id, { hidden: !token.hidden }), ...readPermOps(token, { hidden: !token.hidden })]);
        },
      });
      // com o tracker de combate ativo na mesa
      if (trackerDb) {
        const inCombat = readTracker(trackerDb.visible()).actors.some((a) => a.sceneId === itemId && a.tokenId === token.id);
        entries.push(
          inCombat
            ? { label: t('scene.token.inCombat'), disabled: true, onSelect: () => undefined }
            : { label: t('scene.token.addToCombat'), hint: t('scene.token.addToCombat.hint'), onSelect: () => addToCombat(token) },
        );
      }
      entries.push({ label: t('scene.token.duplicate'), onSelect: () => duplicate(token) });
      entries.push({ label: t('scene.token.front'), onSelect: () => write(restackOps(scene, token.id, 'front')) });
      entries.push({ label: t('scene.token.back'), onSelect: () => write(restackOps(scene, token.id, 'back')) });
      entries.push({ kind: 'separator' });
      entries.push({ label: t('scene.token.delete'), danger: true, onSelect: () => void deleteToken(token) });
    }
    if (entries.length === 1) entries.push({ label: t('scene.token.notYours'), disabled: true, onSelect: () => undefined });
    return entries;
  };

  /* ------------------------------------------------------------------ desenho */

  const dragPos = (token: SceneToken) => {
    if (drag?.kind !== 'token' || drag.id !== token.id || !drag.moved) return { x: token.x, y: token.y };
    // a prévia (e a visão do token arrastado) também para na parede
    return { x: drag.px, y: drag.py };
  };

  /* ------------------------------------------------------------------ visão */

  // quem olha: jogador, espectador, ou o mestre (na vista dele, a névoa mostra o que os jogadores
  // veem, translúcida; em "Ver como", a cena fica igual à de quem foi escolhido)
  const viewer: Viewer | null = !visionOn
    ? null
    : isGm
      ? viewAs && viewAs !== 'all'
        ? { kind: 'player', userId: viewAs }
        : { kind: 'all' }
      : isPlayer && me
        ? { kind: 'player', userId: me.id }
        : { kind: 'spectator' };
  const gmView = isGm && viewAs === null;
  /** vendo como jogador (o próprio, ou o mestre em "Ver como") */
  const asPlayer = visionOn && !gmView;
  const sightBounds = { x: area.x / g, y: area.y / g, w: (area.x2 - area.x) / g, h: (area.y2 - area.y) / g };
  const sight = useSightState(scene, viewer, dragPos, sightBounds, allSegs);
  // as zonas só mudam com o documento (não a cada quadro do arrasto)
  const zones = useMemo(() => zoneGrid(scene.fog, scene.cols, scene.rows), [version]); // eslint-disable-line react-hooks/exhaustive-deps
  /** O token aparece para quem olha? (os dos outros, só no que se vê agora) */
  const tokenShown = (token: SceneToken): boolean => {
    if (token.lightOnly) return isGm && !asPlayer;
    // invisível (BRAZ-24): quem não foi liberado nem recebe o token; o mestre, em "Ver como", vê o mesmo
    if (token.invisible && viewer?.kind === 'player' && isGm && !seesInvisible(token, viewer.userId)) return false;
    if (!asPlayer || !sight) return true;
    if (token.hidden || token.layer === 'gm') return false;
    if (token.layer === 'map') return true;
    // os tokens que dão visão a quem olha sempre aparecem (o centro deles é a ponta do cone)
    if (sight.tokens.some((s) => s.id === token.id)) return true;
    const c = tokenCenter({ ...dragPos(token), size: token.size });
    const zone = zoneAt(zones, scene.cols, scene.rows, c.x, c.y);
    if (zone === ZONE.hide) return false;
    return zone === ZONE.reveal || seesNow(sight, c.x, c.y);
  };
  const selectedSight = selected && sight ? (sight.tokens.find((s) => s.id === selected) ?? null) : null;

  /* ------------------------------------------------------------------ memória */

  // entre os mestres, um só grava (o de menor id entre os que estão na mesa)
  const onlineGms = users.filter((u) => u.id === room?.ownerId || members[u.id]?.modes?.includes('gm')).map((u) => u.id).sort();
  const memoryKeeper = isGm && (!onlineGms.length || onlineGms[0] === me?.id);
  // o mestre que guarda a memória cria o filho `memory` dos tokens que a têm (quem controla o token
  // não cria filhos nele, mas escreve no filho, que herda o `write` do token)
  const needMemory = memoryKeeper && visionOn ? scene.tokens.filter((t) => !t.memoryId && !t.lightOnly && (t.controllers.length > 0 || t.sight.has)).map((t) => t.id) : [];
  const needMemoryKey = needMemory.join(',');
  useEffect(() => {
    const ops = needMemory.flatMap((id) => {
      const token = scene.tokens.find((t) => t.id === id);
      return token ? memoryContainerOps(token, db.newId()) : [];
    });
    if (ops.length) write(ops, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needMemoryKey]);
  // quem grava a memória de cada token (como no original, docs/spec/scene-walls.md): o jogador, a dos
  // tokens dele; o mestre, a dos que têm dono ou "possui visão", mesmo com o dono na mesa (ele pode
  // estar sem a cena aberta, e a memória ficaria com buracos). Os dois chegam aos mesmos bits; o que
  // gravar depois não acha nada novo. Grava o que o token vê de fato: os cones cortados pelas paredes,
  // cruzados com a luz (BRAZ-12; o original grava os cones inteiros, mesmo no escuro).
  const memoryTokens = visionOn
    ? scene.tokens.filter((t) => {
        if (!t.memoryId || t.lightOnly || t.hidden || t.layer === 'gm') return false;
        if (me && t.controllers.includes(me.id)) return true;
        return memoryKeeper && (t.controllers.length > 0 || t.sight.has);
      })
    : [];
  // a luz também muda o que se vê: uma tocha que anda, a luz ambiente
  const lightKey = lightTokens(scene.tokens)
    .map((t) => `${t.id}:${t.x},${t.y},${t.size}:${t.sight.lightBright},${t.sight.lightDim},${t.sight.lightAngle},${t.facing}`)
    .join('|');
  const memoryKey = memoryTokens.length
    ? // a memória apagada ("Reiniciar a exploração") também conta: volta logo o que se vê agora
      `${memoryTokens.map((t) => `${t.id}:${t.x},${t.y},${t.size}:${t.sight.bright},${t.sight.dim},${t.sight.dark},${t.sight.angle},${t.facing}:${Object.keys(t.memory).length > 0}`).join('|')}#${lightKey}#${scene.ambientLight}#${wallsSig}#${scene.cols}x${scene.rows}#${scene.cellDistance}`
    : '';
  useEffect(() => {
    if (!memoryKey) return;
    const timer = setTimeout(() => {
      const ops: Op[] = [];
      const cd = scene.cellDistance;
      // a luz de agora (os polígonos já estão no cache da visão)
      const lit = litTiles(scene.ambientLight, sight?.lights ?? [], scene.cols, scene.rows);
      for (const t of memoryTokens) {
        const c = tokenCenter(t);
        const shapes = tokenSight(t.id, c.x, c.y, { bright: toCells(t.sight.bright, cd), dim: toCells(t.sight.dim, cd), dark: toCells(t.sight.dark, cd) }, allSegs, sightBounds, t.sight.angle < 360 ? { facing: t.facing, angle: t.sight.angle } : undefined);
        const changed = mergeMemory(t.memory, seenTiles(shapes, lit, scene.cols, scene.rows));
        if (Object.keys(changed).length) ops.push(...memoryOps(t, changed));
      }
      // a memória não entra no desfazer
      if (ops.length) write(ops, false);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoryKey]);
  // o esmaecido: a soma das memórias dos tokens que dão visão a quem olha
  const viewerMemories = sight ? scene.tokens.filter((t) => sight.tokens.some((s) => s.id === t.id)).map((t) => t.memory) : [];
  // recalcula só quando o documento muda ou quando muda quem dá visão (não a cada quadro do arrasto)
  const viewerKey = sight ? sight.tokens.map((s) => s.id).join(',') : '';
  const memRects = useMemo(() => memoryRects(viewerMemories), [version, viewerKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const memoryPath = useMemo(() => rectsPath(memRects, g), [memRects, g]);

  /* ------------------------------------------------------------------ portas */

  /** A porta aparece para quem olha? (mestre: todas; os outros: só as que eles enxergam) */
  const doorShown = (wall: Wall): boolean => {
    if (!asPlayer) return true;
    if (wall.door !== 'door' || !sight) return false;
    const c = doorCenter(wall);
    const zone = zoneAt(zones, scene.cols, scene.rows, c.x, c.y);
    if (zone === ZONE.hide) return false;
    if (zone === ZONE.reveal || zone === ZONE.semi) return true;
    // o meio da porta fica na borda do que se vê (a visão para nela): olha um pouco de cada lado
    const [ax, ay, bx, by] = wall.points as [number, number, number, number];
    const len = Math.hypot(bx - ax, by - ay) || 1;
    const nx = (-(by - ay) / len) * 0.1;
    const ny = ((bx - ax) / len) * 0.1;
    return seesNow(sight, c.x + nx, c.y + ny) || seesNow(sight, c.x - nx, c.y - ny);
  };
  const doors = scene.walls.filter((w) => w.door !== 'none' && (isGm || visionOn) && doorShown(w));
  // abre portas quem é mestre, quem é jogador e quem controla um token desta cena (o espectador a
  // quem o mestre deu um personagem: ele já move o token); o servidor confere o mesmo
  const controlsToken = !!me && scene.tokens.some((tk) => tk.controllers.includes(me.id));
  const canUseDoors = tool === 'select' && (isGm || isPlayer || controlsToken);
  const toggleDoor = (wall: Wall) => {
    if (isGm) {
      // trancada, só o botão direito destranca: o clique esquerdo não abre. Olhando como jogador o
      // cadeado não fica à vista, então ele pisca, para mostrar por que a porta não abriu
      if (wall.locked) {
        if (!gmView) flashLock(wall.id);
        return;
      }
      write(doorStateOps(wall, { open: !wall.open }));
    } else sendRaw({ type: 'scene.door', itemId, wallId: wall.id, open: !wall.open });
  };
  // o cadeado por um instante sobre a porta trancada (para quem não o vê sempre)
  const [lockFlash, setLockFlash] = useState<ReadonlySet<string>>(() => new Set());
  const lockTimers = useRef(new Map<string, number>());
  const flashLock = useCallback((wallId: string) => {
    window.clearTimeout(lockTimers.current.get(wallId));
    setLockFlash((s) => new Set(s).add(wallId));
    lockTimers.current.set(
      wallId,
      window.setTimeout(() => {
        lockTimers.current.delete(wallId);
        setLockFlash((s) => {
          const next = new Set(s);
          next.delete(wallId);
          return next;
        });
      }, LOCK_FLASH_MS),
    );
  }, []);
  useEffect(() => {
    const timers = lockTimers.current;
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, []);
  const toggleLock = (wall: Wall) => {
    if (!isGm) return;
    write(doorStateOps(wall, { locked: !wall.locked }));
    // olhando como jogador o cadeado não fica à vista: mostra que trancou
    if (!gmView && !wall.locked) flashLock(wall.id);
  };
  // porta que abre ou fecha faz barulho para quem a vê
  const soundScene = useApp((s) => s.settings.soundScene);
  // o volume dos avisos (Configurações → Notificações), como os outros sons: no padrão (80%), o de antes
  const doorVolume = useApp((s) => 0.75 * s.settings.masterVolume);
  const doorStates = useRef<Map<string, boolean> | null>(null);
  const doorKey = scene.walls.map((w) => `${w.id}:${w.open}`).join('|');
  useEffect(() => {
    const now = new Map(scene.walls.filter((w) => w.door !== 'none' || w.open).map((w) => [w.id, w.open]));
    const before = doorStates.current;
    doorStates.current = now;
    if (!before || !soundScene) return;
    for (const wall of scene.walls) {
      const was = before.get(wall.id);
      if (was === undefined || was === wall.open || !doorShown({ ...wall, door: wall.door === 'none' ? 'door' : wall.door })) continue;
      playDoorSound(wall.open ? 'open' : 'close', doorVolume);
      break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doorKey]);
  // porta trancada: chacoalha para quem tentou; o mestre é avisado
  useEffect(
    () =>
      onSceneDoorLocked((event) => {
        if (event.itemId !== itemId) return;
        if (event.userId === me?.id) {
          flashLock(event.wallId);
          if (soundScene) playDoorSound('locked', doorVolume);
          toast(t('scene.door.isLocked'), 'info');
        } else if (isGm) {
          const who = users.find((u) => u.id === event.userId);
          toast(t('scene.door.lockedNotice', { name: (who && (members[who.id]?.nick ?? who.displayName)) || '—' }), 'info');
        }
      }),
    [itemId, me?.id, isGm, soundScene, doorVolume, users, members, t, flashLock],
  );
  // o jogador mudou o invisível do token dele: o mestre é avisado, como na porta trancada (BRAZ-24)
  useEffect(
    () =>
      onSceneInvisible((event) => {
        if (event.itemId !== itemId || !isGm) return;
        const who = users.find((u) => u.id === event.userId);
        const name = (who && (members[who.id]?.nick ?? who.displayName)) || '—';
        const token = scene.tokens.find((tk) => tk.id === event.tokenId)?.name || t('scene.token');
        toast(t(`scene.invisible.notice.${event.change}`, { name, token }), 'info');
      }),
    [itemId, isGm, users, members, scene.tokens, t],
  );
  const viewAsPlayers = users.filter((u) => members[u.id]?.modes?.includes('player'));

  const fogMaskId = useMemo(() => `fog-${Math.random().toString(36).slice(2, 8)}`, []);
  const gridId = `${fogMaskId}-grid`;
  const markDraft = drag?.kind === 'mark' ? boxOf(drag) : null;
  // alinhando, a grade aparece mesmo desligada (como no ajuste do original)
  const showGrid = scene.gridVisible || tool === 'align';
  const ruler = drag?.kind === 'ruler' ? drag : null;
  /** Uma distância medida na cena (em células), na unidade que a pessoa prefere (`units.ts`). */
  const measure = (cells: number): string => {
    const text = (d: Distance) => {
      const unit = d.unit === 'ft' ? t(Math.round(d.value * 10) === 10 ? 'scene.unit.foot' : 'scene.unit.feet') : d.unit === 'm' ? t('scene.unit.m') : scene.cellUnit;
      return `${formatNumber(d.value)} ${unit}`;
    };
    const [first, second] = distanceParts(cells * scene.cellDistance, scene.cellUnit, distanceUnit);
    return second ? `${text(first!)} (${text(second)})` : text(first!);
  };
  const moving = drag?.kind === 'token' && drag.moved && drag.track ? { track: drag.track, size: scene.tokens.find((tk) => tk.id === drag.id)?.size ?? 1 } : null;
  const fogDraft = drag?.kind === 'fog' ? cellRect(drag.ax, drag.ay, drag.bx, drag.by) : null;
  const cursor = drag?.kind === 'pan' ? 'grabbing' : tool === 'pan' ? 'grab' : tool === 'select' ? 'default' : tool === 'erase' ? 'cell' : 'crosshair';
  const drawDraft = drag?.kind === 'draw' ? drag : null;

  // `hint`: o que a ferramenta faz, na dica do botão. Selecionar e mover e arrastar a vista são as duas
  // do original ("Navegar"); a dica deixa clara a diferença entre elas (BRAZ-8)
  const tools: { id: Tool; label: string; icon: string; hint?: string; gm?: boolean; draw?: boolean; bg?: boolean; vision?: boolean }[] = [
    { id: 'select', label: t('scene.tool.select'), hint: t('scene.tool.select.hint'), icon: 'M5 3l12 9-5 1 3 6-2 1-3-6-4 4z' },
    { id: 'pan', label: t('scene.tool.pan'), hint: t('scene.tool.pan.hint'), icon: 'M12 2l3 3h-2v6h6V9l3 3-3 3v-2h-6v6h2l-3 3-3-3h2v-6H5v2l-3-3 3-3v2h6V5H9z' },
    { id: 'ruler', label: t('scene.tool.ruler'), icon: 'M3 17L17 3l4 4L7 21zm3-1l1.5 1.5M8.5 13.5l1.5 1.5M11 11l2.5 2.5M13.5 8.5 15 10M16 6l1.5 1.5' },
    { id: 'reveal', label: t('scene.tool.reveal'), icon: 'M12 5C6 5 2 12 2 12s4 7 10 7 10-7 10-7-4-7-10-7zm0 11a4 4 0 110-8 4 4 0 010 8z', gm: true },
    { id: 'hide', label: t('scene.tool.hide'), icon: 'M3 4l17 17M10.6 6.1A10 10 0 0112 6c6 0 10 6 10 6a17 17 0 01-3 3.6M6.6 7.6A17 17 0 002 12s4 6 10 6a9 9 0 004-1', gm: true },
    { id: 'semi', label: t('scene.tool.semi'), hint: t('scene.tool.semi.hint'), icon: 'M12 5C6 5 2 12 2 12s4 7 10 7 10-7 10-7-4-7-10-7zM12 5v14', gm: true, vision: true },
    { id: 'explore', label: t('scene.tool.explore'), hint: t('scene.tool.explore.hint'), icon: 'M12 3l2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12l6.5-2.5z', gm: true, vision: true },
    { id: 'draw', label: t('scene.tool.draw'), icon: 'M4 20l4-1 11-11-3-3L5 16zM14 7l3 3', draw: true },
    { id: 'erase', label: t('scene.tool.erase'), icon: 'M8 20h12M5 15l8-8 6 6-5 5H9z', draw: true },
    { id: 'align', label: t('scene.tool.align'), icon: 'M3 3h18v18H3zM3 9h18M3 15h18M9 3v18M15 3v18M10.5 12h3M12 10.5v3', gm: true, bg: true },
    { id: 'walls', label: t('scene.tool.walls'), icon: 'M3 5h18v14H3zM3 9.7h18M3 14.3h18M9 5v4.7M15 5v4.7M6 9.7v4.6M12 9.7v4.6M18 9.7v4.6M9 14.3V19M15 14.3V19', gm: true },
  ];
  const shownTools = tools.filter((tl) => (!tl.gm || isGm) && (!tl.draw || canDraw) && (!tl.bg || !!bgRect) && (!tl.vision || visionOn));
  const fogTools = shownTools.filter((tl) => FOG_TOOLS.includes(tl.id));
  const fogShown = fogTools.find((tl) => tl.id === tool) ?? fogTools.find((tl) => tl.id === lastFogTool.current) ?? fogTools[0];
  // o que está sendo desenhado ou arrastado na ferramenta "Paredes"
  const wallDraft: WallDraft =
    drag?.kind === 'wallShape'
      ? drag.shape === 'free'
        ? { points: drag.points, closed: false }
        : { points: drag.shape === 'rect' ? rectPoints(drag.ax, drag.ay, drag.bx, drag.by) : ellipsePoints(drag.ax, drag.ay, drag.bx, drag.by), closed: true }
      : wallPath
        ? { points: wallHover && tool === 'walls' ? [...wallPath, wallHover.x, wallHover.y] : wallPath, closed: false }
        : null;
  const wallBox = drag?.kind === 'wallBox' ? { x: Math.min(drag.ax, drag.bx), y: Math.min(drag.ay, drag.by), w: Math.abs(drag.bx - drag.ax), h: Math.abs(drag.by - drag.ay), cut: drag.cut } : null;
  const shownWalls =
    drag?.kind === 'wallVertex' && drag.moved
      ? scene.walls.map((w) => {
          if (w.id !== drag.wallId) return w;
          const points = w.points.slice();
          points[drag.index * 2] = drag.x;
          points[drag.index * 2 + 1] = drag.y;
          return { ...w, points };
        })
      : scene.walls;

  // os tokens que dão visão a quem olha ficam por cima da névoa (o próprio token nunca some nela)
  const shownTokens = tokens.filter(tokenShown);
  const aboveFog = new Set(asPlayer && sight ? sight.tokens.map((x) => x.id) : []);
  // névoa sem a visão dos tokens: para o jogador, o token debaixo dela não recebe o ponteiro (o
  // clique passa para o mapa, como se ele não estivesse ali). A névoa em si não segura o ponteiro,
  // senão o jogador não pegaria nem os tokens da área revelada
  const underFog = (token: SceneToken): boolean => {
    if (isGm || visionOn || !scene.fogEnabled) return false;
    const c = tokenCenter({ ...dragPos(token), size: token.size });
    return zoneAt(zones, scene.cols, scene.rows, c.x, c.y) !== ZONE.reveal;
  };
  // a aura acompanha o token (também no arraste) e fica embaixo da camada dele
  const renderAura = (token: SceneToken) => {
    const p = dragPos(token);
    return <AuraView key={`aura-${token.id}`} token={token} x={p.x} y={p.y} g={g} zoom={view.zoom} cellDistance={scene.cellDistance} />;
  };
  const renderToken = (token: SceneToken) => {
    const p = dragPos(token);
    return (
      <TokenView
        key={token.id}
        token={token}
        x={p.x * g}
        y={p.y * g}
        g={g}
        zoom={view.zoom}
        selected={selected === token.id}
        inert={underFog(token)}
        movable={canMoveToken(token, me?.id, isGm) && tool === 'select'}
        onPointerDown={onTokenDown(token)}
        onDoubleClick={() => canMoveToken(token, me?.id, isGm) && setEditing(token.id)}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // o Android também solta o contextmenu ao segurar o dedo: o menu abre uma vez, e o token não vai junto com o dedo
          cancelHold();
          setDrag(null);
          setSelected(token.id);
          setMenu({ anchor: { x: e.clientX, y: e.clientY }, tokenId: token.id });
        }}
      />
    );
  };

  return (
    <div className="rd-scene">
      <div className="rd-scene__toolbar" role="toolbar" aria-label={t('module.scene')}>
        <div className="rd-scene__group">
          {shownTools.map((tl) =>
            !FOG_TOOLS.includes(tl.id) ? (
              <button
                key={tl.id}
                type="button"
                className={`rd-scene__tool${tool === tl.id ? ' is-on' : ''}`}
                aria-pressed={tool === tl.id}
                title={tl.hint ? `${tl.label}\n${tl.hint}` : tl.label}
                aria-label={tl.label}
                onClick={() => setTool(tl.id)}
              >
                <ToolIcon id={tl.id} path={tl.icon} />
              </button>
            ) : tl === fogTools[0] && fogShown ? (
              // as de névoa: um botão no lugar da primeira, que abre o menu com todas
              <button
                key="fog"
                type="button"
                className={`rd-scene__tool rd-scene__tool--menu${fogShown.id === tool ? ' is-on' : ''}`}
                aria-haspopup="menu"
                aria-expanded={!!fogMenu}
                title={`${t('scene.tool.fog')}\n${fogShown.label}`}
                aria-label={t('scene.tool.fog')}
                // com o menu aberto, o mousedown aqui o fecharia e o clique o abriria de novo: quem fecha é o clique
                onMouseDown={(e) => fogMenu && e.stopPropagation()}
                onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  setFogMenu(fogMenu ? null : { x: r.left, y: r.bottom + 4 });
                }}
              >
                <ToolIcon id={fogShown.id} path={fogShown.icon} />
                <svg className="rd-scene__caret" width="8" height="8" viewBox="0 0 8 8" aria-hidden="true">
                  <path d="M1 2.5h6L4 6z" fill="currentColor" />
                </svg>
              </button>
            ) : null,
          )}
        </div>
        {isGm ? (
          <div className="rd-scene__group">
            <span className="rd-scene__sep" />
            <button type="button" className="rd-scene__tool rd-scene__tool--text" onClick={() => addToken()} title={t('scene.addToken')} aria-label={t('scene.token')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                <circle cx="12" cy="12" r="8" />
                <path d="M12 8v8M8 12h8" strokeLinecap="round" />
              </svg>
              <span className="rd-scene__label">{t('scene.token')}</span>
            </button>
            <button
              type="button"
              className="rd-scene__tool rd-scene__tool--text"
              onClick={() => setBgPicker(true)}
              title={t('scene.background')}
              aria-label={t('scene.mapImage')}
              disabled={busy}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <rect x="3" y="4" width="18" height="16" rx="1.5" />
                <path d="M3 16l5-5 4 4 3-3 6 6" strokeLinejoin="round" />
              </svg>
              <span className="rd-scene__label">{t('scene.mapImage')}</span>
            </button>
            {scene.fogEnabled ? (
              <button
                type="button"
                className="rd-scene__tool rd-scene__tool--text"
                title={t('scene.fog.off')}
                aria-label={t('scene.fog.off')}
                onClick={() => write([...setOps('root', { fogEnabled: false }), ...clearFogOps()])}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                  <path d="M4 9h11M4 13h16M7 17h9M18 5l-3 3M6 5l12 12" />
                </svg>
                <span className="rd-scene__label">{t('scene.fog.off')}</span>
              </button>
            ) : null}
            {visionOn ? (
              <select className="rd-scene__select" aria-label={t('scene.viewAs')} title={t('scene.viewAs')} value={viewAs ?? ''} onChange={(e) => setViewAs(e.target.value || null)}>
                <option value="">{t('scene.viewAs.gm')}</option>
                <option value="all">{t('scene.viewAs.all')}</option>
                {viewAsPlayers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {members[u.id]?.nick ?? u.displayName}
                  </option>
                ))}
              </select>
            ) : null}
            <button type="button" className="rd-scene__tool" title={t('scene.settings')} aria-label={t('scene.settings')} onClick={() => setSettings(true)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm7.4-3a7.4 7.4 0 0 0-.1-1.2l2-1.6-2-3.4-2.4 1a7.4 7.4 0 0 0-2-1.2L14.5 3h-5l-.4 2.6a7.4 7.4 0 0 0-2 1.2l-2.4-1-2 3.4 2 1.6a7.4 7.4 0 0 0 0 2.4l-2 1.6 2 3.4 2.4-1a7.4 7.4 0 0 0 2 1.2l.4 2.6h5l.4-2.6a7.4 7.4 0 0 0 2-1.2l2.4 1 2-3.4-2-1.6c.1-.4.1-.8.1-1.2Z" />
              </svg>
            </button>
          </div>
        ) : null}
        <span className="rd-scene__grow" />
        <div className="rd-scene__group">
          <button type="button" className="rd-scene__tool" title={t('scene.undo')} aria-label={t('scene.undo')} disabled={!undoStack.current.length} onClick={undo}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 14 4 9l5-5M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
            </svg>
          </button>
          <button type="button" className="rd-scene__tool" title={t('scene.redo')} aria-label={t('scene.redo')} disabled={!redoStack.current.length} onClick={redo}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m15 14 5-5-5-5M20 9H9.5a5.5 5.5 0 0 0 0 11H13" />
            </svg>
          </button>
          <span className="rd-scene__sep" />
          {busy ? <span className="rd-spinner" aria-label={t('scene.uploading')} /> : null}
          <button type="button" className="rd-scene__tool rd-scene__zoomStep" title={t('scene.zoomOut')} aria-label={t('scene.zoomOut')} onClick={() => zoomAt(1 / 1.25)}>
            −
          </button>
          <button type="button" className="rd-scene__zoom" title={t('scene.fit')} onClick={fit}>
            {Math.round(view.zoom * 100)}%
          </button>
          <button type="button" className="rd-scene__tool rd-scene__zoomStep" title={t('scene.zoomIn')} aria-label={t('scene.zoomIn')} onClick={() => zoomAt(1.25)}>
            +
          </button>
        </div>
      </div>
      {isGm && scene.bgLegacy && bgHref ? (
        <div className="rd-scene__subbar rd-scene__legacy" role="status">
          <span className="rd-scene__hintText">{t('scene.align.legacy')}</span>
          <button type="button" className="rd-scene__chip is-on" disabled={busy} onClick={() => void detectAgain()}>
            {t('scene.align.legacyFix')}
          </button>
        </div>
      ) : null}
      {tool === 'align' && isGm && bgRect ? (
        <div className="rd-scene__subbar" role="toolbar" aria-label={t('scene.align.title')}>
          <span className="rd-scene__subgroup">
            <span className="rd-scene__hintText">{t('scene.align.width')}</span>
            <button type="button" className="rd-scene__chip" aria-label={t('scene.align.narrower')} title={t('scene.align.narrower')} onClick={(e) => nudge({ w: bgRect.w - alignStep(e) })}>
              −
            </button>
            <output className="rd-scene__value" aria-label={t('scene.align.widthValue')}>
              {cells(bgRect.w)}
            </output>
            <button type="button" className="rd-scene__chip" aria-label={t('scene.align.wider')} title={t('scene.align.wider')} onClick={(e) => nudge({ w: bgRect.w + alignStep(e) })}>
              +
            </button>
          </span>
          <span className="rd-scene__subgroup">
            <span className="rd-scene__hintText">{t('scene.align.height')}</span>
            <button type="button" className="rd-scene__chip" aria-label={t('scene.align.shorter')} title={t('scene.align.shorter')} onClick={(e) => nudge({ h: bgRect.h - alignStep(e) })}>
              −
            </button>
            <output className="rd-scene__value" aria-label={t('scene.align.heightValue')}>
              {cells(bgRect.h)}
            </output>
            <button type="button" className="rd-scene__chip" aria-label={t('scene.align.taller')} title={t('scene.align.taller')} onClick={(e) => nudge({ h: bgRect.h + alignStep(e) })}>
              +
            </button>
          </span>
          <span className="rd-scene__subgroup">
            {(
              [
                ['left', '←', -1, 0],
                ['right', '→', 1, 0],
                ['up', '↑', 0, -1],
                ['down', '↓', 0, 1],
              ] as const
            ).map(([dir, arrow, dx, dy]) => (
              <button
                key={dir}
                type="button"
                className="rd-scene__chip"
                aria-label={t(`scene.align.move.${dir}`)}
                title={t(`scene.align.move.${dir}`)}
                onClick={(e) => nudge({ x: bgRect.x + dx * alignStep(e), y: bgRect.y + dy * alignStep(e) })}
              >
                {arrow}
              </button>
            ))}
          </span>
          <span className="rd-scene__subgroup">
            <span className="rd-scene__hintText">{t('scene.align.angle')}</span>
            <button type="button" className="rd-scene__chip" aria-label={t('scene.align.turnLeft')} title={t('scene.align.turnLeft')} onClick={(e) => nudge({ angle: (bgRect.angle ?? 0) - angleStep(e) })}>
              ↺
            </button>
            <output className="rd-scene__value" aria-label={t('scene.align.angleValue')}>
              {degrees(bgRect.angle ?? 0)}
            </output>
            <button type="button" className="rd-scene__chip" aria-label={t('scene.align.turnRight')} title={t('scene.align.turnRight')} onClick={(e) => nudge({ angle: (bgRect.angle ?? 0) + angleStep(e) })}>
              ↻
            </button>
          </span>
          <span className="rd-scene__subgroup">
            <span className="rd-scene__hintText">{t('scene.align.marks', { n: marks.length })}</span>
            <button type="button" className="rd-scene__chip" disabled={!marks.length || busy} onClick={() => void syncMarks()}>
              {t('scene.align.sync')}
            </button>
            {marks.length ? (
              <button type="button" className="rd-scene__chip" onClick={() => setMarks([])}>
                {t('scene.align.clearMarks')}
              </button>
            ) : null}
          </span>
          <span className="rd-scene__subgroup">
            <button type="button" className="rd-scene__chip" disabled={busy || !bgHref} onClick={() => void detectAgain()}>
              {t('scene.align.detect')}
            </button>
            {boardFor(bgRect).cols !== scene.cols || boardFor(bgRect).rows !== scene.rows ? (
              <button type="button" className="rd-scene__chip" onClick={() => write(setOps('root', boardFor(bgRect)))}>
                {t('scene.align.fitBoard')}
              </button>
            ) : null}
          </span>
          <span className="rd-scene__hintText rd-scene__alignHint">{t('scene.align.hint')}</span>
        </div>
      ) : null}
      {tool === 'walls' && isGm ? (
        <div className="rd-scene__subbar" role="toolbar" aria-label={t('scene.walls.options')}>
          <span className="rd-scene__subgroup">
            {(['draw', 'select', 'cut'] as WallMode[]).map((m) => (
              <button key={m} type="button" className={`rd-scene__chip${wallMode === m ? ' is-on' : ''}`} aria-pressed={wallMode === m} onClick={() => setWallMode(m)}>
                {t(`scene.walls.mode.${m}`)}
              </button>
            ))}
          </span>
          {wallMode === 'draw' ? (
            <span className="rd-scene__subgroup" role="radiogroup" aria-label={t('scene.walls.prop.type')}>
              {PRESET_ORDER.map((p) => (
                <button
                  key={p}
                  type="button"
                  role="radio"
                  aria-checked={wallPreset === p}
                  className={`rd-scene__chip${wallPreset === p ? ' is-on' : ''}`}
                  onClick={() => setWallPreset(p)}
                >
                  <span className="rd-scene__dot" style={{ background: WALL_COLORS[p] }} />
                  {t(`scene.walls.preset.${p}`)}
                </button>
              ))}
            </span>
          ) : null}
          {wallMode === 'draw' ? (
            <span className="rd-scene__subgroup">
              {(['line', 'rect', 'ellipse', 'free'] as WallShape[]).map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`rd-scene__chip${shapeInUse === k ? ' is-on' : ''}`}
                  aria-pressed={shapeInUse === k}
                  disabled={doorPreset && k !== 'line'}
                  onClick={() => setWallShape(k)}
                >
                  {t(`scene.walls.shape.${k}`)}
                </button>
              ))}
            </span>
          ) : null}
          <span className="rd-scene__subgroup">
            <label className="rd-scene__check">
              <input type="checkbox" checked={wallSnap} onChange={(e) => setWallSnap(e.target.checked)} /> {t('scene.walls.snap')}
            </label>
          </span>
          <span className="rd-scene__hintText rd-scene__alignHint">{t(`scene.walls.hint.${wallMode === 'draw' ? (shapeInUse === 'line' ? 'line' : 'shape') : wallMode}`)}</span>
          <span className="rd-scene__grow" />
          {selectedWalls.size ? (
            <button type="button" className="rd-scene__chip" onClick={() => setWallDialog([...selectedWalls])}>
              {t('scene.walls.propertiesButton')}
            </button>
          ) : null}
          {selectedWalls.size ? (
            <button type="button" className="rd-scene__chip" onClick={deleteSelectedWalls}>
              {t('scene.walls.deleteSelected', { n: selectedWalls.size })}
            </button>
          ) : null}
          <button type="button" className="rd-scene__chip" disabled={!scene.walls.length} title={t('scene.walls.join.hint')} onClick={joinWallEnds}>
            {t('scene.walls.join')}
          </button>
          <button type="button" className="rd-scene__chip" disabled={!scene.walls.length} onClick={() => void clearWalls()}>
            {t('scene.walls.clear')}
          </button>
        </div>
      ) : null}
      {(tool === 'draw' || tool === 'erase') && canDraw ? (
        <div className="rd-scene__subbar" role="toolbar" aria-label={t('scene.draw.options')}>
          {tool === 'draw' ? (
            <>
              <span className="rd-scene__subgroup">
                {(['free', 'rect', 'ellipse', 'text'] as DrawingKind[]).map((k) => (
                  <button key={k} type="button" className={`rd-scene__chip${drawKind === k ? ' is-on' : ''}`} aria-pressed={drawKind === k} onClick={() => setDrawKind(k)}>
                    {t(`scene.draw.${k}`)}
                  </button>
                ))}
              </span>
              <span className="rd-scene__subgroup">
              {TOKEN_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`rd-scene__swatch${drawColor === c ? ' is-on' : ''}`}
                  style={{ background: c }}
                  aria-label={t('scene.draw.color', { color: c })}
                  aria-pressed={drawColor === c}
                  onClick={() => setDrawColor(c)}
                />
              ))}
              </span>
              <span className="rd-scene__subgroup">
              {(['s', 'm', 'l'] as DrawWidth[]).map((w) => (
                <button key={w} type="button" className={`rd-scene__chip${drawWidth === w ? ' is-on' : ''}`} aria-pressed={drawWidth === w} onClick={() => setDrawWidth(w)}>
                  {t(`scene.draw.width.${w}`)}
                </button>
              ))}
              {drawKind === 'rect' || drawKind === 'ellipse' ? (
                <label className="rd-scene__check">
                  <input type="checkbox" checked={drawFill} onChange={(e) => setDrawFill(e.target.checked)} /> {t('scene.draw.fill')}
                </label>
              ) : null}
              </span>
            </>
          ) : (
            <span className="rd-scene__hintText">{t('scene.draw.eraseHint')}</span>
          )}
          <span className="rd-scene__grow" />
          <button
            type="button"
            className="rd-scene__chip"
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setClearMenu({ x: r.left, y: r.bottom + 4 });
            }}
          >
            {t('scene.draw.clear')}
          </button>
        </div>
      ) : null}

      <div
        ref={viewportRef}
        className="rd-scene__viewport"
        tabIndex={0}
        aria-label={t('scene.viewport')}
        style={{ cursor }}
        onPointerDownCapture={onTouchDown}
        onPointerDown={onViewportDown}
        onPointerMove={onMove}
        onPointerUp={(e) => {
          if (!onTouchEnd(e)) onUp();
        }}
        onPointerCancel={(e) => {
          if (onTouchEnd(e)) return;
          cancelHold();
          altClickRef.current = null;
          setDrag(null);
        }}
        onContextMenu={(e) => e.preventDefault()}
        onDoubleClick={(e) => {
          if (tool === 'walls' && wallMode === 'draw' && wallPath) finishWallPath();
          if (tool === 'walls' && wallMode === 'select' && isGm) {
            const c = toCell(e);
            const wall = hitWall(scene.walls, c.x, c.y, 6 / view.zoom / g);
            if (wall) setWallDialog(selectedWalls.has(wall.id) ? [...selectedWalls] : [wall.id]);
          }
        }}
        onKeyDown={onKeyDown}
        onKeyUp={(e) => {
          if (e.key !== 'Alt' || !altPinged.current) return;
          altPinged.current = false;
          e.preventDefault();
        }}
        onDragOver={(e) => {
          if (isGm && (e.dataTransfer.types.includes('Files') || hasFileDrag(e.dataTransfer))) e.preventDefault();
        }}
        onDrop={(e) => {
          e.preventDefault();
          const cloudFile = readFileDrag(e.dataTransfer);
          if (cloudFile) void onDropCloudFile(cloudFile, e);
          else void onDropFiles(e.dataTransfer.files, e, e.dataTransfer.getData(AVATAR_DRAG_NAME));
        }}
      >
        <svg className="rd-scene__svg" width="100%" height="100%">
          <defs>
            <pattern id={gridId} width={g} height={g} patternUnits="userSpaceOnUse">
              <path d={`M ${g} 0 L 0 0 0 ${g}`} fill="none" stroke={scene.gridColor} strokeOpacity={scene.gridOpacity} strokeWidth={1 / view.zoom} />
            </pattern>
            <mask id={fogMaskId} maskUnits="userSpaceOnUse" x={area.x} y={area.y} width={area.x2 - area.x} height={area.y2 - area.y}>
              <rect x={area.x} y={area.y} width={area.x2 - area.x} height={area.y2 - area.y} fill="white" />
              {scene.fog.map((f) => (
                <rect key={f.id} x={f.x * g} y={f.y * g} width={f.w * g} height={f.h * g} fill={f.mode === 'reveal' ? 'black' : 'white'} />
              ))}
            </mask>
          </defs>
          <g transform={`translate(${view.x} ${view.y}) scale(${view.zoom})`}>
            <rect className="rd-scene__floor" width={mapW} height={mapH} />
            {bgHref && bgRect ? (
              <image
                className="rd-scene__bg"
                href={bgHref}
                x={bgRect.x * g}
                y={bgRect.y * g}
                width={bgRect.w * g}
                height={bgRect.h * g}
                preserveAspectRatio="none"
                transform={bgRect.angle ? `rotate(${bgRect.angle} ${(bgRect.x + bgRect.w / 2) * g} ${(bgRect.y + bgRect.h / 2) * g})` : undefined}
              />
            ) : null}
            {showGrid ? (
              <g pointerEvents="none">
                <rect width={mapW} height={mapH} fill={`url(#${gridId})`} />
                {/* o padrão desenha a borda de cima e a da esquerda de cada célula: fecha a última coluna e linha */}
                <path d={`M ${mapW} 0 V ${mapH} H 0`} fill="none" stroke={scene.gridColor} strokeOpacity={scene.gridOpacity} strokeWidth={1 / view.zoom} />
              </g>
            ) : null}
            <g className="rd-scene__drawings">
              {scene.drawings.map((d) => {
                const erasable = tool === 'erase' && canEditDrawing(d, me?.id, isGm);
                return (
                  <DrawingView
                    key={d.id}
                    d={d}
                    erasable={erasable}
                    onPointerDown={(e) => {
                      // Alt + clique é ping, também sobre um rabisco
                      if (!erasable || e.button !== 0 || e.altKey) return;
                      e.stopPropagation();
                      eraseDrawing(d);
                    }}
                  />
                );
              })}
              {drawDraft ? (
                <DrawingView
                  d={{
                    id: 'draft',
                    kind: drawDraft.shape,
                    points: drawDraft.points,
                    x: Math.min(drawDraft.ax, drawDraft.bx),
                    y: Math.min(drawDraft.ay, drawDraft.by),
                    w: Math.abs(drawDraft.bx - drawDraft.ax),
                    h: Math.abs(drawDraft.by - drawDraft.ay),
                    text: '',
                    stroke: drawColor,
                    fill: drawFill && drawDraft.shape !== 'free' ? `${drawColor}55` : null,
                    width: g * STROKE[drawWidth],
                    owner: null,
                  }}
                  erasable={false}
                />
              ) : null}
            </g>
            {/* camada por camada: as auras logo abaixo dos tokens dela (as dos tokens que ficam por
                cima da névoa também, e então a névoa as cobre onde quem olha não enxerga) */}
            {TOKEN_LAYERS.map((layer) => (
              <Fragment key={layer}>
                {shownTokens.filter((tk) => tk.layer === layer && tk.aura).map(renderAura)}
                {shownTokens.filter((tk) => tk.layer === layer && !aboveFog.has(tk.id)).map(renderToken)}
              </Fragment>
            ))}
            {visionOn && sight ? <VisionFog st={sight} scene={scene} zones={zones} g={g} area={area} ids={`${fogMaskId}-v`} gmView={gmView} memoryPath={memoryPath} memoryCount={memorySize(memRects)} /> : null}
            {selectedSight ? <SightOutline shapes={selectedSight} g={g} zoom={view.zoom} gmView={gmView} /> : null}
            {shownTokens.filter((tk) => aboveFog.has(tk.id)).map(renderToken)}
            {scene.fogEnabled && !visionOn ? (
              <rect
                className={isGm ? 'rd-scene__fog rd-scene__fog--gm' : 'rd-scene__fog'}
                // a névoa cobre também o pedaço da imagem fora da grade
                x={area.x}
                y={area.y}
                width={area.x2 - area.x}
                height={area.y2 - area.y}
                mask={`url(#${fogMaskId})`}
                pointerEvents="none"
              />
            ) : null}
            {fogDraft ? (
              <rect
                className={`rd-scene__fogDraft${tool === 'hide' ? ' is-hide' : ''}`}
                x={fogDraft.x * g}
                y={fogDraft.y * g}
                width={fogDraft.w * g}
                height={fogDraft.h * g}
                strokeWidth={2 / view.zoom}
              />
            ) : null}
            {isGm && !asPlayer ? (
              <WallsLayer walls={shownWalls} g={g} zoom={view.zoom} selected={selectedWalls} showVertices={tool === 'walls' && wallMode === 'select'} draft={tool === 'walls' ? wallDraft : null} box={wallBox} />
            ) : null}
            {doors.length ? (
              <DoorsLayer
                doors={doors}
                g={g}
                zoom={view.zoom}
                interactive={canUseDoors}
                showLocks={gmView}
                flashing={lockFlash}
                labels={{ open: t('scene.door.open'), close: t('scene.door.close'), locked: t(isGm ? 'scene.door.locked.gm' : 'scene.door.locked') }}
                onToggle={toggleDoor}
                onLock={isGm ? toggleLock : undefined}
              />
            ) : null}
            {tool === 'align'
              ? [...marks, ...(markDraft ? [markDraft] : [])].map((m, i) => (
                  <rect key={i} className="rd-scene__mark" x={m.x} y={m.y} width={m.w} height={m.h} strokeWidth={1.5 / view.zoom} pointerEvents="none" />
                ))
              : null}
            {moving ? (
              <MovePath
                track={moving.track}
                size={moving.size}
                g={g}
                zoom={view.zoom}
                measure={measure}
                bounds={{ x0: -view.x / view.zoom, y0: -view.y / view.zoom, x1: (viewSize.w - view.x) / view.zoom, y1: (viewSize.h - view.y) / view.zoom }}
              />
            ) : null}
            {ruler ? <Ruler r={ruler} g={g} zoom={view.zoom} measure={measure} /> : null}
            {pings.map((p) => {
              const { name, color } = pingOf(p);
              return <Ping key={p.key} x={p.x * g} y={p.y * g} g={g} zoom={view.zoom} name={name} color={color} />;
            })}
          </g>
          {/* ping fora da vista: a seta na borda, em px da tela */}
          {pings.map((p) => {
            const edge = pingEdge(view.x + p.x * g * view.zoom, view.y + p.y * g * view.zoom, viewSize.w, viewSize.h);
            if (!edge) return null;
            const { name, color } = pingOf(p);
            return <PingEdge key={p.key} {...edge} w={viewSize.w} h={viewSize.h} name={name} color={color} />;
          })}
        </svg>
        {tokens.length === 0 && !scene.bgFileId && !scene.bgUrl ? <div className="rd-scene__hint">{isGm ? t('scene.empty.gm') : t('scene.empty')}</div> : null}
      </div>

      {menu
        ? (() => {
            const token = scene.tokens.find((tk) => tk.id === menu.tokenId);
            return token ? <ContextMenu anchor={menu.anchor} entries={tokenMenu(token)} label={token.name} onClose={() => setMenu(null)} /> : null;
          })()
        : null}
      {fogMenu && fogTools.length ? (
        <ContextMenu
          anchor={fogMenu}
          label={t('scene.tool.fog')}
          onClose={() => setFogMenu(null)}
          entries={fogTools.map((tl) => ({ label: tl.label, hint: tl.hint, icon: <ToolIcon id={tl.id} path={tl.icon} />, checked: tool === tl.id, onSelect: () => setTool(tl.id) }))}
        />
      ) : null}
      {clearMenu ? (
        <ContextMenu
          anchor={clearMenu}
          label={t('scene.draw.clear')}
          onClose={() => setClearMenu(null)}
          entries={[
            { label: t('scene.draw.clear.mine'), onSelect: () => void clearDrawings('mine') },
            ...(isGm
              ? [
                  { label: t('scene.draw.clear.players'), onSelect: () => void clearDrawings('players') },
                  { label: t('scene.draw.clear.all'), danger: true, onSelect: () => void clearDrawings('all') },
                ]
              : []),
          ]}
        />
      ) : null}
      {editing
        ? (() => {
            const token = scene.tokens.find((tk) => tk.id === editing);
            return token ? (
              <TokenDialog
                token={token}
                isGm={isGm}
                unit={scene.cellUnit}
                cellDistance={scene.cellDistance}
                onClose={() => setEditing(null)}
                write={write}
                newId={() => db.newId()}
                setInvisible={(viewers) => sendRaw({ type: 'scene.invisible', itemId, tokenId: token.id, viewers })}
              />
            ) : null;
          })()
        : null}
      {invisibleFor
        ? (() => {
            const token = scene.tokens.find((tk) => tk.id === invisibleFor);
            return token ? (
              <InvisibleDialog
                token={token}
                onClose={() => setInvisibleFor(null)}
                onSave={(viewers) => {
                  sendRaw({ type: 'scene.invisible', itemId, tokenId: token.id, viewers });
                  setInvisibleFor(null);
                }}
              />
            ) : null;
          })()
        : null}
      {wallDialog && scene.walls.some((w) => wallDialog.includes(w.id)) ? (
        <WallDialog walls={scene.walls.filter((w) => wallDialog.includes(w.id))} onClose={() => setWallDialog(null)} write={write} newId={() => db.newId()} />
      ) : null}
      {settings ? <SceneSettingsDialog scene={scene} onClose={() => setSettings(false)} write={write} onPickImage={() => setBgPicker(true)} onImportUvtt={(file) => void importUvtt(file)} /> : null}
      {bgPicker ? <ImagePickerDialog title={t('scene.bg.pick')} initial={scene.bgUrl ?? undefined} onPick={(value) => void setBackground(value)} onCancel={() => setBgPicker(false)} /> : null}
    </div>
  );
}

/** Ícone de uma ferramenta da barra: as duas de navegação são cheias; as outras, de traço. */
function ToolIcon({ id, path }: { id: Tool; path: string }) {
  const solid = id === 'select' || id === 'pan';
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill={solid ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={solid ? 0 : 1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={path} />
    </svg>
  );
}

function TokenView({
  token,
  x,
  y,
  g,
  zoom,
  selected,
  inert = false,
  movable,
  onPointerDown,
  onDoubleClick,
  onContextMenu,
}: {
  token: SceneToken;
  x: number;
  y: number;
  g: number;
  zoom: number;
  selected: boolean;
  /** debaixo da névoa, para o jogador: o ponteiro passa direto (vai para o mapa) */
  inert?: boolean;
  movable: boolean;
  onPointerDown: (e: RPointerEvent<SVGGElement>) => void;
  onDoubleClick: () => void;
  onContextMenu: (e: React.MouseEvent<SVGGElement>) => void;
}) {
  const url = useFileUrl(token.fileId);
  const s = token.size * g;
  const r = s / 2;
  const clipId = `tk-${token.id.replace(/[^a-z0-9]/gi, '')}`;
  const initials = token.name.trim().slice(0, 2).toUpperCase() || '?';
  const hpRatio = token.hp !== null && token.hpMax ? Math.max(0, Math.min(1, token.hp / token.hpMax)) : null;
  const label = tokenNameSize(g, zoom);
  return (
    <g
      className={`rd-token${selected ? ' is-selected' : ''}${movable ? ' is-movable' : ''}${token.hidden ? ' is-hidden' : ''}${token.layer === 'gm' ? ' is-gm' : ''}${token.lightOnly ? ' is-light' : ''}${token.invisible ? ' is-invisible' : ''}`}
      transform={`translate(${x} ${y})`}
      pointerEvents={inert ? 'none' : undefined}
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
      data-token={token.name}
      data-facing={token.facing || undefined}
      data-invisible={token.invisible ? '' : undefined}
    >
      <clipPath id={clipId}>
        <circle cx={r} cy={r} r={r - 2} />
      </clipPath>
      <circle className="rd-token__base" cx={r} cy={r} r={r - 1} fill={token.color} />
      {/* direção: gira a imagem ou mostra uma seta (como as duas opções do original) */}
      <g transform={token.facing && token.sight.facingMode === 'rotate' ? `rotate(${token.facing} ${r} ${r})` : undefined}>
        {token.fileId && typeof url === 'string' ? (
          <image href={url} x={2} y={2} width={s - 4} height={s - 4} clipPath={`url(#${clipId})`} preserveAspectRatio="xMidYMid slice" />
        ) : (
          <text className="rd-token__initials" x={r} y={r} fontSize={s * 0.36} dominantBaseline="central" textAnchor="middle">
            {initials}
          </text>
        )}
      </g>
      <circle className="rd-token__ring" cx={r} cy={r} r={r - 1} strokeWidth={Math.max(2, 3 / zoom)} />
      {token.sight.facingMode === 'arrow' ? (
        <path
          className="rd-token__arrow"
          d={`M${r} ${-s * 0.14}L${r - s * 0.11} ${s * 0.07}L${r + s * 0.11} ${s * 0.07}Z`}
          transform={`rotate(${token.facing} ${r} ${r})`}
          strokeWidth={Math.max(1, 1.5 / zoom)}
        />
      ) : null}
      {hpRatio !== null ? (
        <g transform={`translate(${s * 0.1} ${-Math.max(6, 8 / zoom)})`}>
          <rect className="rd-token__barBg" width={s * 0.8} height={Math.max(4, 5 / zoom)} rx={2 / zoom} />
          <rect
            className="rd-token__bar"
            width={s * 0.8 * hpRatio}
            height={Math.max(4, 5 / zoom)}
            rx={2 / zoom}
            fill={hpRatio > 0.5 ? '#5fbf4a' : hpRatio > 0.25 ? '#e3b23b' : '#e5413b'}
          />
        </g>
      ) : null}
      {token.showName && token.name ? (
        <text className="rd-token__name" x={r} y={s + label * 0.9} fontSize={label} textAnchor="middle" strokeWidth={label * 0.28}>
          {token.name}
        </text>
      ) : null}
      <ConditionBadges ids={tokenConditions(token)} s={s} zoom={zoom} />
    </g>
  );
}

/**
 * A aura do token (`aura.ts`): a forma pintada na cor dele, translúcida, com o contorno mais forte;
 * ou a imagem. Não pega o ponteiro (o clique vai para o mapa ou para o token).
 */
function AuraView({ token, x, y, g, zoom, cellDistance }: { token: SceneToken; x: number; y: number; g: number; zoom: number; cellDistance: number }) {
  const aura = token.aura!;
  const imageUrl = useFileUrl(aura.shape === 'image' ? aura.fileId : null);
  const drawing = auraDrawing({ x, y, size: token.size, facing: token.facing }, aura, cellDistance, g);
  if (!drawing) return null;
  // a aura do token que os jogadores não veem: apagada, como ele
  const faded = token.hidden || token.layer === 'gm';
  const common = {
    className: `rd-aura${faded ? ' is-hidden' : ''}`,
    'data-aura': token.name,
    'data-aura-shape': aura.shape,
    pointerEvents: 'none' as const,
  };
  if (drawing.kind === 'image') {
    const href = aura.fileId ? (typeof imageUrl === 'string' ? imageUrl : null) : aura.url;
    if (!href) return null;
    return (
      <image
        {...common}
        href={href}
        x={drawing.x}
        y={drawing.y}
        width={drawing.w}
        height={drawing.h}
        // no estilo, para valer por cima do apagado da classe (que é só das formas)
        style={{ opacity: aura.opacity * (faded ? 0.5 : 1) }}
        preserveAspectRatio="xMidYMid meet"
        transform={drawing.angle ? `rotate(${drawing.angle} ${drawing.cx} ${drawing.cy})` : undefined}
      />
    );
  }
  const paint = { fill: aura.color, fillOpacity: aura.opacity, stroke: aura.color, strokeOpacity: Math.min(1, aura.opacity + 0.4), strokeWidth: 1.5 / zoom };
  if (drawing.kind === 'circle') return <circle {...common} {...paint} cx={drawing.cx} cy={drawing.cy} r={drawing.r} />;
  if (drawing.kind === 'path') return <path {...common} {...paint} strokeLinejoin="round" d={drawing.d} />;
  return (
    <g {...common} data-aura-cells={drawing.count}>
      <path d={drawing.fill} fill={aura.color} fillOpacity={aura.opacity} />
      <path d={drawing.outline} fill="none" stroke={aura.color} strokeOpacity={paint.strokeOpacity} strokeWidth={paint.strokeWidth} strokeLinecap="square" />
    </g>
  );
}

/** Um desenho da cena; com a borracha, os que a pessoa pode apagar recebem clique. */
function DrawingView({ d, erasable, onPointerDown }: { d: SceneDrawing; erasable: boolean; onPointerDown?: (e: RPointerEvent<SVGElement>) => void }) {
  const common = {
    className: `rd-drawing${erasable ? ' is-erasable' : ''}`,
    'data-drawing': d.kind,
    pointerEvents: erasable ? ('visiblePainted' as const) : ('none' as const),
    onPointerDown,
  };
  if (d.kind === 'text') {
    return (
      <text {...common} x={d.x} y={d.y} fontSize={d.width} fill={d.stroke} dominantBaseline="hanging" strokeWidth={d.width * 0.12}>
        {d.text}
      </text>
    );
  }
  const stroke = { stroke: d.stroke, strokeWidth: d.width, fill: d.fill ?? 'none', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (d.kind === 'free') {
    const pts: string[] = [];
    for (let i = 0; i + 1 < d.points.length; i += 2) pts.push(`${d.points[i]},${d.points[i + 1]}`);
    return <polyline {...common} {...stroke} fill="none" points={pts.join(' ')} />;
  }
  if (d.kind === 'ellipse') return <ellipse {...common} {...stroke} cx={d.x + d.w / 2} cy={d.y + d.h / 2} rx={d.w / 2} ry={d.h / 2} />;
  return <rect {...common} {...stroke} x={d.x} y={d.y} width={d.w} height={d.h} />;
}

/**
 * Pedra caindo na água: um respingo no ponto e três ondas que se abrem até `PING_RADIUS` células,
 * afinando e sumindo, com o nome de quem pingou. As linhas têm espessura fixa na tela.
 */
function Ping({ x, y, g, zoom, name, color }: { x: number; y: number; g: number; zoom: number; name: string; color: string }) {
  const fs = 13 / zoom;
  const r = g * PING_RADIUS;
  return (
    <g className="rd-ping" transform={`translate(${x} ${y})`} pointerEvents="none" data-ping={name}>
      <circle className="rd-ping__splash" r={g * 0.4} fill={color} />
      {[0, 1, 2].map((i) => (
        <g key={i} className="rd-ping__wave" style={{ animationDelay: `${i * 0.4}s` }}>
          <circle className="rd-ping__shadow" r={r} />
          <circle className="rd-ping__ring" r={r} stroke={color} />
        </g>
      ))}
      <circle className="rd-ping__dot" r={Math.max(4 / zoom, g * 0.08)} fill={color} />
      {name ? (
        <text className="rd-ping__name" y={-g * 0.35} fontSize={fs} textAnchor="middle" strokeWidth={fs * 0.28}>
          {name}
        </text>
      ) : null}
    </g>
  );
}

/** Ping fora da vista (como no original): uma seta na borda apontando para ele, com o nome. */
function PingEdge({ x, y, angle, w, h, name, color }: { x: number; y: number; angle: number; w: number; h: number; name: string; color: string }) {
  const a = (angle * Math.PI) / 180;
  // o nome fica para dentro da vista, sem sair dela
  const lx = Math.min(w - 60, Math.max(60, x - Math.cos(a) * 32));
  const ly = Math.min(h - 8, Math.max(16, y - Math.sin(a) * 32 + 4));
  return (
    <g className="rd-ping rd-ping--edge" pointerEvents="none" data-ping-edge={name}>
      <g transform={`translate(${x} ${y})`}>
        <circle className="rd-ping__edgeRing" r={12} stroke={color} />
        <circle r={5} fill={color} stroke="rgb(0 0 0 / 55%)" strokeWidth={1.5} />
        <path d="M28 0L12 -9L12 9Z" fill={color} stroke="rgb(0 0 0 / 55%)" strokeWidth={1.5} transform={`rotate(${angle})`} />
      </g>
      {name ? (
        <text className="rd-ping__name" x={lx} y={ly} fontSize={13} textAnchor="middle" strokeWidth={3.6}>
          {name}
        </text>
      ) : null}
    </g>
  );
}

function Ruler({ r, g, zoom, measure }: { r: { ax: number; ay: number; bx: number; by: number }; g: number; zoom: number; measure: (cells: number) => string }) {
  const ax = (Math.floor(r.ax) + 0.5) * g;
  const ay = (Math.floor(r.ay) + 0.5) * g;
  const bx = (Math.floor(r.bx) + 0.5) * g;
  const by = (Math.floor(r.by) + 0.5) * g;
  const cells = gridDistance(Math.floor(r.ax), Math.floor(r.ay), Math.floor(r.bx), Math.floor(r.by));
  const text = measure(cells);
  const fs = RULER_FONT_PX / zoom;
  return (
    <g className="rd-scene__ruler" pointerEvents="none">
      <line x1={ax} y1={ay} x2={bx} y2={by} strokeWidth={3 / zoom} />
      <circle cx={ax} cy={ay} r={4 / zoom} />
      <circle cx={bx} cy={by} r={4 / zoom} />
      <g transform={`translate(${bx + 10 / zoom} ${by - 10 / zoom})`}>
        <rect x={-4 / zoom} y={-fs} width={(textWidth(text, RULER_FONT_PX, 500) + 8) / zoom} height={fs * 1.4} rx={3 / zoom} />
        <text fontSize={fs} y={0}>
          {text}
        </text>
      </g>
    </g>
  );
}

/**
 * O caminho do token arrastado, como no original (`rrpgScene_MovementPath.dlua`): as células por
 * onde ele passou, a linha com a seta na ponta e, acima dela, a distância somada.
 */
function MovePath({
  track,
  size,
  g,
  zoom,
  measure,
  bounds,
}: {
  track: MoveTrack;
  size: number;
  g: number;
  zoom: number;
  measure: (cells: number) => string;
  /** o que se vê do mapa, em px do mapa */
  bounds: { x0: number; y0: number; x1: number; y1: number };
}) {
  const d = drawTrack(track);
  if (!d) return null;
  const text = measure(d.cost);
  const px = (v: number) => v / zoom;
  const fs = px(LABEL_FONT_PX);
  const lineH = fs * 1.2;
  const textW = px(textWidth(text, LABEL_FONT_PX));
  const boxW = textW + px(6);
  const ex = d.end.x * g;
  const ey = d.end.y * g;
  const margin = px(4);
  // acima do token, como no original (meio token mais 8 px, pelo menos 20 px), e acima da
  // barrinha de vida, que aqui fica logo em cima do token
  let top = ey - Math.max((size * g) / 2 + Math.max(6, px(8)) + px(8), px(20)) - lineH;
  // **(RPG Desk)** sem espaço acima (o token no alto da vista), embaixo dele, depois do nome
  if (top - px(2) < bounds.y0 + margin) top = ey + (size * g) / 2 + tokenNameSize(g, zoom) * 1.3 + px(6);
  // e sem sair pelos lados
  const left = Math.min(Math.max(ex - textW / 2 - px(2), bounds.x0 + margin), bounds.x1 - margin - boxW);
  const pts = (flat: number[]) => flat.map((v) => v * g).join(' ');
  return (
    <g className="rd-movepath" pointerEvents="none">
      {d.cells.map((c) => (
        <rect key={`${c.i},${c.j}`} className="rd-movepath__cell" x={c.i * g} y={c.j * g} width={g} height={g} />
      ))}
      {d.lines.map((line, k) => (
        <polyline key={k} className="rd-movepath__line" points={pts(line)} strokeWidth={px(3)} />
      ))}
      <polyline className="rd-movepath__line" points={pts([d.arrow[0].x, d.arrow[0].y, d.end.x, d.end.y, d.arrow[1].x, d.arrow[1].y])} strokeWidth={px(3)} />
      <rect className="rd-movepath__box" x={left} y={top - px(2)} width={boxW} height={lineH + px(6)} />
      <text className="rd-movepath__text" x={left + px(2) + textW / 2} y={top + lineH / 2 + px(1)} fontSize={fs} textAnchor="middle" dominantBaseline="central">
        {text}
      </text>
    </g>
  );
}

/** Letra do nome embaixo do token, em px do mapa. */
const tokenNameSize = (g: number, zoom: number) => Math.max(11, Math.min(16, g * 0.22)) / Math.max(zoom, 0.6);

/** Letra da distância do caminho, em px da tela (negrito, como no original). */
const LABEL_FONT_PX = 18;
/** Letra da régua, em px da tela (peso 500, no CSS). */
const RULER_FONT_PX = 14;
let measureCtx: CanvasRenderingContext2D | null | undefined;
let labelFamily: string | undefined;
/** Largura do texto na letra da interface, em px da tela (medida no canvas; sem ele, estimada). */
function textWidth(text: string, sizePx: number, weight = 700): number {
  if (measureCtx === undefined) measureCtx = document.createElement('canvas').getContext('2d');
  if (!measureCtx) return text.length * sizePx * 0.6;
  labelFamily ??= getComputedStyle(document.documentElement).getPropertyValue('--font-default').trim() || 'sans-serif';
  measureCtx.font = `${weight} ${sizePx}px ${labelFamily}`;
  return measureCtx.measureText(text).width;
}

const round2 = (v: number) => Math.round(v * 100) / 100;
const clampTo = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), Math.max(lo, hi));
const formatNumber = (v: number) => v.toLocaleString(document.documentElement.lang || undefined, { maximumFractionDigits: 1 });
